import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor, subtabAccessibleFor, moduleCanDo } from "../lib/modules";
import { toast } from "sonner";
import { Plus, Search, Phone, Mail, MapPin, Building2, ArrowUpRight, Eye, Link2, ChevronDown, ChevronRight, Users, X, PhoneCall, Inbox, Calendar, Download, Trash2 } from "lucide-react";
import ClientFormDialog from "../components/ClientFormDialog";
import LeadCallRequestButton from "../components/LeadCallRequestButton";
import ClientImportantRemark from "../components/ClientImportantRemark";
import GeMSupportDialog from "../components/GeMSupportDialog";
import QuoteRequestDialog from "../components/QuoteRequestDialog";
import { Store, FileText, GraduationCap, Send, ShieldCheck } from "lucide-react";
import { CrmStageBadge, CrmStageButtons, CrmStageFilterChips, deriveStage, STAGE_KEYS } from "../components/CrmStage";
import { TrainingRequestDialog, emptyReqForm } from "./Trainers";
import ContactDeleteRequests from "./ContactDeleteRequests";

const CONNECT_METHODS = ["Email", "WhatsApp", "Call", "Visit", "Other"];
const LEAD_STATUSES = ["HOT", "Converted to Sales", "Cold", "Not Interested", "SPAM"];
const LEAD_COLOR = {
  "HOT": "bg-rose-600 text-white border-rose-700 shadow-sm shadow-rose-300/40 hover:bg-rose-700",
  "Converted to Sales": "bg-emerald-600 text-white border-emerald-700 shadow-sm shadow-emerald-300/40 hover:bg-emerald-700",
  "Cold": "bg-sky-600 text-white border-sky-700 shadow-sm shadow-sky-300/40 hover:bg-sky-700",
  "Not Interested": "bg-slate-500 text-white border-slate-600 hover:bg-slate-600",
  "SPAM": "bg-amber-600 text-white border-amber-700 shadow-sm shadow-amber-300/40 hover:bg-amber-700",
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

export default function CRMReporting() {
  const { user } = useAuth();
  const isAdmin = user?.role === "master_admin" || moduleCanDo(user, "sales_crm_report", "edit");
  // Mapping Requests — kept scoped to CRM Client editors.
  const canMapping = user?.role === "master_admin" || moduleCanDo(user, "crm_clients", "edit");
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [allClients, setAllClients] = useState([]);
  // v109.9.93 — Unified All-Team "View" segmented control replaces the two
  // stacked chip rows (client-status + follow-up). One control, one axis.
  const [allTeamView, setAllTeamView] = useState("all"); // all | with_logs | no_log | fup_missed | fup_today | fup_upcoming
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "clients");
  // Keep URL in sync so deep links like /m/crm_reports?tab=contact-approvals work
  useEffect(() => {
    const current = searchParams.get("tab");
    if (tab !== "clients") {
      if (current !== tab) setSearchParams({ tab }, { replace: true });
    } else if (current) {
      setSearchParams({}, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);
  const [viewMode] = useGlobalViewMode();
  const [search, setSearch] = useState("");
  const [leadFilter, setLeadFilter] = useState("all");
  const [followFilter, setFollowFilter] = useState("all");
  const [submitterFilter, setSubmitterFilter] = useState("all");
  // v109.9.92 — All-Team date filter: mode + date/month/range values
  const todayIso = new Date().toISOString().slice(0, 10);
  const monthIso = todayIso.slice(0, 7);
  const [dateMode, setDateMode] = useState("all"); // all | day | month | range
  const [dateDay, setDateDay] = useState(todayIso);
  const [dateMonth, setDateMonth] = useState(monthIso);
  const [dateStart, setDateStart] = useState(todayIso);
  const [dateEnd, setDateEnd] = useState(todayIso);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [active, setActive] = useState(null); // client being logged
  const [history, setHistory] = useState([]);
  const [gemOpen, setGemOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  // v107 — Need Trainer request (creates a training-request that lands in Trainers → Approval)
  const [needTrainerOpen, setNeedTrainerOpen] = useState(false);
  const [needTrainerForm, setNeedTrainerForm] = useState(emptyReqForm());
  // Trainers + active employees list (fetched once, on-demand)
  const [trainersList, setTrainersList] = useState([]);
  const [activeEmployees, setActiveEmployees] = useState([]);

  // v109.9.35 — "Have Funds" flow — updates client DB and enables Hot toggle
  const [fundsClient, setFundsClient] = useState(null); // { client, amount, is_hot }

  // v109.9.35 — Call-Through / guided dialer
  const [callThroughOpen, setCallThroughOpen] = useState(false);
  const [callSession, setCallSession] = useState(null); // { queue: [id...], visited: Set, current: client }
  const [statusFlags, setStatusFlags] = useState({}); // { [client_id]: { quote: {stage}, gem: {status} } }
  const [form, setForm] = useState(emptyForm());
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [detail, setDetail] = useState(null); // client in detail view
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailExtra, setDetailExtra] = useState(null); // { orders, documentation_assignees, current_doc_stage }
  const [mapOpen, setMapOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [collapsedEmp, setCollapsedEmp] = useState({});
  const [mappingReqs, setMappingReqs] = useState([]);
  const [hotClients, setHotClients] = useState([]);
  const [salesEligible, setSalesEligible] = useState([]);
  const [collabOpen, setCollabOpen] = useState(null);   // client object for collaboration dialog
  const [collabPick, setCollabPick] = useState("");
  const [collabRemark, setCollabRemark] = useState("");
  const [collabPeople, setCollabPeople] = useState([]); // directory pool

  // Log Report → contact management state
  const [logContacts, setLogContacts] = useState([]); // contacts of the active client
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", number: "", email: "", role: "" });
  const [deleteContactReq, setDeleteContactReq] = useState(null); // {contact, remark}
  const [transferContactReq, setTransferContactReq] = useState(null); // {contact, target_udise, remark, lookup}
  const [contactBusy, setContactBusy] = useState(false);

  const loadSalesEligible = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/crm/sales-eligible-users");
      setSalesEligible(data || []);
    } catch (e) { /* silent */ }
  };

  const assignSalesOwner = async (client, userId) => {
    if (!userId) return;
    const u = salesEligible.find((x) => x.id === userId);
    if (!u) return;
    if (!window.confirm(`Assign "${client.client_name}" to ${u.name}?`)) return;
    try {
      await api.post(`/crm/clients/${client.id}/assign-sales`, { user_id: userId });
      toast.success(`Assigned to ${u.name} ✓`);
      loadHotClients();
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const loadHotClients = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/crm/hot-clients");
      setHotClients(data || []);
    } catch (e) { /* silent */ }
  };

  // ---------- Collaboration helpers ----------
  const openCollab = async (c) => {
    setCollabOpen(c);
    setCollabPick(""); setCollabRemark("");
    if (collabPeople.length === 0) {
      try { const { data } = await api.get("/directory"); setCollabPeople(data || []); } catch { /* */ }
    }
  };
  const saveCollab = async () => {
    if (!collabPick) { toast.error("Pick an employee to collaborate with"); return; }
    if (!collabRemark.trim()) { toast.error("Add a remark explaining what help is needed"); return; }
    try {
      await api.post(`/crm/clients/${collabOpen.id}/collaborators`, { user_id: collabPick, remark: collabRemark.trim() });
      toast.success("Collaborator added");
      setCollabOpen(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const removeCollab = async (clientId, userId) => {
    if (!window.confirm("Remove this collaborator?")) return;
    try {
      await api.delete(`/crm/clients/${clientId}/collaborators/${userId}`);
      toast.success("Collaborator removed");
      // Refresh both the table and the open dialog (if still showing this client)
      if (collabOpen?.id === clientId) {
        const refreshed = await api.get("/crm/my-assigned-clients");
        setClients(refreshed.data);
        const fresh = refreshed.data.find((x) => x.id === clientId);
        setCollabOpen(fresh || null);
      } else { load(); }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/crm/my-assigned-clients"),
        api.get("/records/sales_crm_report"),
      ]);
      setClients(c.data);
      // STRICT: All My Reports must show only what the current user submitted.
      const uid = user?.id;
      setReports(uid ? r.data.filter((x) => x.created_by_id === uid) : r.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const loadAllReports = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/crm/all-reports");
      setAllReports(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.9.92 — Download the filtered All-Team logs as a PDF. The current
  // date-mode + submitter + lead filters are forwarded to the backend so the
  // downloaded file matches exactly what's rendered on-screen.
  const downloadAllReportsPdf = async () => {
    if (!isAdmin || downloadingPdf) return;
    const params = new URLSearchParams();
    params.set("mode", dateMode);
    if (dateMode === "day") params.set("date", dateDay);
    if (dateMode === "month") params.set("month", dateMonth);
    if (dateMode === "range") { params.set("start", dateStart); params.set("end", dateEnd); }
    if (submitterFilter !== "all") params.set("submitter_id", submitterFilter);
    if (leadFilter !== "all") params.set("lead_status", leadFilter);
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/crm/all-reports/export.pdf?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = dateMode === "day" ? dateDay
        : dateMode === "month" ? dateMonth
        : dateMode === "range" ? `${dateStart}_to_${dateEnd}`
        : "all";
      a.download = `crm_all_team_logs_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const loadMappingReqs = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/crm/reassign-requests");
      setMappingReqs(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const decideMapping = async (id, approve) => {
    try {
      await api.post(`/crm/reassign-requests/${id}/decision`, { approve });
      toast.success(approve ? "Mapping approved" : "Mapping rejected");
      loadMappingReqs();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [user?.id]);
  useEffect(() => {
    // Fetch latest Quote + GeM request status for every client in one shot
    // so we can render the status tag without N+1 calls.
    (async () => {
      try {
        const { data } = await api.get("/crm/client-status-flags");
        setStatusFlags(data || {});
      } catch { /* silently ignore — tags are nice-to-have */ }
    })();
  }, [user?.id]);
  useEffect(() => { if (tab === "all-team" && isAdmin) loadAllReports(); }, [tab, isAdmin]);
  // Load all clients (admin-only) for the "no log report" count on All Team tab
  useEffect(() => {
    if (tab === "all-team" && isAdmin) {
      api.get("/records/crm_clients").then(({ data }) => setAllClients(data || [])).catch(() => setAllClients([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);
  useEffect(() => { if (tab === "mapping" && canMapping) loadMappingReqs(); }, [tab, canMapping]);
  useEffect(() => { if (tab === "super-hot" && isAdmin) { loadHotClients(); loadSalesEligible(); } /* eslint-disable-next-line */ }, [tab, isAdmin]);

  const [noLogOnly, setNoLogOnly] = useState(false);
  const [clientTypeFilter, setClientTypeFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [followupFilter, setFollowupFilter] = useState("all"); // all | overdue | today | upcoming

  // Compute the next follow-up date per client using the LATEST log filed.
  // We also expose the entire latest-report object so the table can render
  // "Last Log · <status> · <notes>" per row.
  const { followupByClient, lastLogByClient } = useMemo(() => {
    const fu = new Map();
    const last = new Map();
    // Sort by created_at descending — most recent first.
    const sorted = [...reports].sort((a, b) => String(b.created_at || b.submitted_at || "").localeCompare(String(a.created_at || a.submitted_at || "")));
    sorted.forEach((r) => {
      const keys = [r.client_id, r.u_dise].filter(Boolean);
      keys.forEach((k) => {
        if (!last.has(k)) last.set(k, r); // first seen (newest) wins
        if (r.next_follow_up && !fu.has(k)) fu.set(k, r.next_follow_up);
      });
    });
    return { followupByClient: fu, lastLogByClient: last };
  }, [reports]);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const followupCounts = useMemo(() => {
    const counts = { total: clients.length, overdue: 0, today: 0, upcoming: 0, none: 0 };
    clients.forEach((c) => {
      const d = followupByClient.get(c.id) || followupByClient.get(c.u_dise);
      if (!d) { counts.none++; return; }
      if (d < todayStr) counts.overdue++;
      else if (d === todayStr) counts.today++;
      else counts.upcoming++;
    });
    return counts;
  }, [clients, followupByClient, todayStr]);

  const filteredClients = useMemo(() => {
    const q = search.toLowerCase();
    let list = clients.filter((c) => !q ||
      [c.client_name, c.u_dise, c.district, c.state, c.client_type].some((v) => String(v ?? "").toLowerCase().includes(q))
    );
    if (clientTypeFilter !== "all") {
      list = list.filter((c) => {
        const types = [c.client_type, ...(c.client_types || [])].filter(Boolean);
        return types.includes(clientTypeFilter);
      });
    }
    if (noLogOnly) {
      const clientsWithLogs = new Set(reports.map((r) => r.u_dise || r.client_id).filter(Boolean));
      list = list.filter((c) => !clientsWithLogs.has(c.u_dise) && !clientsWithLogs.has(c.id));
    }
    if (stageFilter !== "all") {
      list = list.filter((c) => (c.crm_stage || deriveStage(c)) === stageFilter);
    }
    if (followupFilter !== "all") {
      list = list.filter((c) => {
        const d = followupByClient.get(c.id) || followupByClient.get(c.u_dise);
        if (followupFilter === "overdue") return d && d < todayStr;
        if (followupFilter === "today") return d === todayStr;
        if (followupFilter === "upcoming") return d && d > todayStr;
        return true;
      });
    }
    return list;
  }, [clients, reports, search, noLogOnly, clientTypeFilter, stageFilter, followupFilter, followupByClient, todayStr]);

  // Counts for the stage filter chips (used in CRM Reporting)
  const stageCounts = useMemo(() => {
    const counts = { total: clients.length };
    STAGE_KEYS.forEach((k) => { counts[k] = 0; });
    clients.forEach((c) => {
      const s = c.crm_stage || deriveStage(c);
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [clients]);

  // Distinct client types present in the user's assigned client roster, used
  // to render the filter chip set dynamically (avoids surfacing chips for
  // types that don't appear in this user's data).
  const availableClientTypes = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => {
      const types = [c.client_type, ...(c.client_types || [])].filter(Boolean);
      types.forEach((t) => set.add(t));
    });
    return Array.from(set).sort();
  }, [clients]);

  const filteredReports = useMemo(() => {
    let list = reports;
    if (leadFilter !== "all") list = list.filter((r) => r.lead_status === leadFilter);
    const t = new Date().toISOString().slice(0, 10);
    if (followFilter === "today") list = list.filter((r) => r.next_follow_up === t);
    else if (followFilter === "overdue") list = list.filter((r) => r.next_follow_up && r.next_follow_up < t);
    else if (followFilter === "upcoming") list = list.filter((r) => r.next_follow_up && r.next_follow_up > t);
    const q = search.toLowerCase();
    if (q) list = list.filter((r) => [r.client_name, r.remark, r.created_by_name].some((v) => String(v ?? "").toLowerCase().includes(q)));
    return list;
  }, [reports, search, leadFilter, followFilter]);

  const submitterOptions = useMemo(() => {
    const set = new Map();
    allReports.forEach((r) => { if (r.created_by_name) set.set(r.created_by_id || r.created_by_name, r.created_by_name); });
    return Array.from(set.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allReports]);

  // v109.9.92 — Resolve the date-mode into concrete inclusive bounds.
  const dateBounds = useMemo(() => {
    if (dateMode === "day" && dateDay) return { start: dateDay, end: dateDay };
    if (dateMode === "month" && dateMonth) {
      const [y, m] = dateMonth.split("-").map((x) => parseInt(x, 10));
      const last = new Date(y, m, 0).getDate();
      return { start: `${dateMonth}-01`, end: `${dateMonth}-${String(last).padStart(2, "0")}` };
    }
    if (dateMode === "range" && dateStart && dateEnd) {
      const s = dateStart <= dateEnd ? dateStart : dateEnd;
      const e = dateStart <= dateEnd ? dateEnd : dateStart;
      return { start: s, end: e };
    }
    return null;
  }, [dateMode, dateDay, dateMonth, dateStart, dateEnd]);

  const filteredAll = useMemo(() => {
    let list = allReports;
    if (leadFilter !== "all") list = list.filter((r) => r.lead_status === leadFilter);
    if (submitterFilter !== "all") list = list.filter((r) => (r.created_by_id || r.created_by_name) === submitterFilter);
    if (dateBounds) {
      list = list.filter((r) => {
        const day = (r.created_at || "").slice(0, 10);
        return day && day >= dateBounds.start && day <= dateBounds.end;
      });
    }
    // Note: follow-up narrowing is applied at the client-bucket level via
    // `allTeamView` — see groupedAll below. This keeps the toolbar to a single
    // "View" control (v109.9.93).
    const q = search.toLowerCase();
    if (q) list = list.filter((r) => [r.client_name, r.u_dise, r.remark, r.created_by_name].some((v) => String(v ?? "").toLowerCase().includes(q)));
    return list;
  }, [allReports, leadFilter, submitterFilter, search, dateBounds]);

  const groupedAll = useMemo(() => {
    // Build a map: clientId -> { client, logs[] }
    const today = new Date().toISOString().slice(0, 10);
    const byClient = new Map();
    // Seed every assigned client (so "no log" clients appear too)
    (allClients || []).forEach((c) => {
      if (!c.is_approved) return; // only approved clients
      const key = c.id;
      byClient.set(key, {
        client: c,
        logs: [],
        latest: null,
        no_log: true,
        followup_missed: false,
      });
    });
    // Bucket each report into its client (match by client_id, fallback to u_dise)
    filteredAll.forEach((r) => {
      let key = r.client_id;
      if (!key || !byClient.has(key)) {
        // Fallback via u_dise
        const match = (allClients || []).find((c) => c.u_dise && c.u_dise === r.u_dise);
        if (match) key = match.id;
      }
      if (!key) return; // orphan log; skip
      if (!byClient.has(key)) return;
      const bucket = byClient.get(key);
      bucket.logs.push(r);
      bucket.no_log = false;
    });
    // Compute followup status + latest log
    byClient.forEach((b) => {
      if (b.logs.length === 0) return;
      b.logs.sort((a, x) => (x.created_at || "").localeCompare(a.created_at || ""));
      b.latest = b.logs[0];
      const nf = b.latest.next_follow_up;
      // Follow-up missed: the latest log's next_follow_up is set AND in the past
      if (nf && nf < today) b.followup_missed = true;
      if (nf === today) b.followup_today = true;
      if (nf && nf > today) b.followup_upcoming = true;
    });
    // Apply the unified "View" filter (v109.9.93 — one segmented control).
    // v109.9.165 — When a specific submitter is picked, drop clients that
    // have zero logs from that user (previously we still showed every
    // assigned client with an empty log list, so the "user filter" felt
    // broken). This makes the User selector behave like "show only this
    // user's logs" as the docs promise.
    const list = Array.from(byClient.values()).filter((b) => {
      if (submitterFilter !== "all" && b.no_log) return false;
      if (allTeamView === "no_log") return b.no_log;
      if (allTeamView === "with_logs") return !b.no_log;
      if (allTeamView === "fup_missed") return b.followup_missed;
      if (allTeamView === "fup_today") return b.followup_today;
      if (allTeamView === "fup_upcoming") return b.followup_upcoming;
      return true;
    });
    list.sort((a, b) => (a.client.client_name || "").localeCompare(b.client.client_name || ""));
    return list;
  }, [filteredAll, allClients, allTeamView, submitterFilter]);

  // v109.9.93 — Counts feeding the unified "View" segmented control in the
  // All-Team tab. Reflects the current search / date / submitter / lead
  // narrowing so the badges are always contextual to the visible dataset.
  const allTeamViewCounts = useMemo(() => {
    const approvedClients = (allClients || []).filter((c) => c.is_approved);
    const today = new Date().toISOString().slice(0, 10);
    const filteredClientIds = new Set(filteredAll.map((r) => r.client_id).filter(Boolean));
    let withLogs = 0;
    let noLog = 0;
    let missed = 0;
    let dueToday = 0;
    let upcoming = 0;
    const latest = new Map();
    filteredAll.forEach((r) => {
      if (!r.client_id) return;
      const prev = latest.get(r.client_id);
      if (!prev || (r.created_at || "") > (prev.created_at || "")) latest.set(r.client_id, r);
    });
    approvedClients.forEach((c) => {
      if (filteredClientIds.has(c.id)) withLogs += 1;
      else noLog += 1;
      const l = latest.get(c.id);
      if (!l) return;
      const nf = l.next_follow_up;
      if (nf && nf < today) missed += 1;
      else if (nf === today) dueToday += 1;
      else if (nf && nf > today) upcoming += 1;
    });
    return {
      all: approvedClients.length,
      with_logs: withLogs,
      no_log: noLog,
      fup_missed: missed,
      fup_today: dueToday,
      fup_upcoming: upcoming,
    };
  }, [allClients, filteredAll]);

  // Re-group the filtered client buckets by assigned employee → Employee → Schools → Logs.
  const groupedByEmployee = useMemo(() => {
    const byEmp = new Map(); // empKey -> { employee_id, employee_name, clients: [] }
    groupedAll.forEach((b) => {
      const empId = b.client.assigned_to_id || "_unassigned";
      const empName = b.client.assigned_to_name || "Unassigned";
      if (!byEmp.has(empId)) {
        byEmp.set(empId, { employee_id: empId, employee_name: empName, clients: [], total_logs: 0, no_log_count: 0, missed_count: 0 });
      }
      const e = byEmp.get(empId);
      e.clients.push(b);
      e.total_logs += b.logs.length;
      if (b.no_log) e.no_log_count += 1;
      if (b.followup_missed) e.missed_count += 1;
    });
    const arr = Array.from(byEmp.values());
    arr.forEach((e) => e.clients.sort((a, b) => (a.client.client_name || "").localeCompare(b.client.client_name || "")));
    arr.sort((a, b) => (a.employee_name || "").localeCompare(b.employee_name || ""));
    return arr;
  }, [groupedAll]);

  const openLog = async (client) => {
    setActive(client);
    setForm({ ...emptyForm(), client_id: client.id, client_name: client.client_name, u_dise: client.u_dise || "" });
    setAddContactOpen(false);
    setNewContact({ name: "", number: "", email: "", role: "" });
    setDeleteContactReq(null);
    setTransferContactReq(null);
    try {
      const [reportsRes, ctxRes] = await Promise.all([
        api.get(`/crm/clients/${client.id}/reports`),
        api.get(`/clients/${client.id}/order-context`),
      ]);
      setHistory(reportsRes.data || []);
      setLogContacts(Array.isArray(ctxRes.data?.contacts) ? ctxRes.data.contacts : []);
    } catch {
      setHistory([]);
      setLogContacts([]);
    }
  };

  const refreshLogContacts = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/clients/${active.id}/order-context`);
      setLogContacts(Array.isArray(data?.contacts) ? data.contacts : []);
    } catch { /* silent */ }
  };

  const submitAddContact = async () => {
    if (!active) return;
    const number = (newContact.number || "").trim();
    if (!number) { toast.error("Contact number is required"); return; }
    setContactBusy(true);
    try {
      await api.post(`/clients/${active.id}/contacts`, newContact);
      toast.success("Contact added to client database");
      setAddContactOpen(false);
      setNewContact({ name: "", number: "", email: "", role: "" });
      await refreshLogContacts();
      // Auto-select the newly added contact as the connected contact
      setForm((f) => ({ ...f, connected_contact_number: number, connected_contact_name: newContact.name || "" }));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setContactBusy(false); }
  };

  const submitDeleteRequest = async () => {
    if (!deleteContactReq) return;
    const remark = (deleteContactReq.remark || "").trim();
    if (remark.length < 5) { toast.error("Remark must be at least 5 characters"); return; }
    setContactBusy(true);
    try {
      await api.post(`/clients/${active.id}/contacts/request-delete`, {
        contact_number: deleteContactReq.contact.number,
        contact_name: deleteContactReq.contact.name,
        remark,
      });
      toast.success("Deletion request submitted for admin approval");
      setDeleteContactReq(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setContactBusy(false); }
  };

  const lookupTransferTarget = async () => {
    if (!transferContactReq) return;
    const code = (transferContactReq.target_udise || "").trim();
    if (!code) { toast.error("Enter the target school's U-DISE"); return; }
    try {
      const { data } = await api.get(`/crm/udise-check/${encodeURIComponent(code)}`);
      setTransferContactReq((t) => ({ ...t, lookup: data }));
      if (!data?.exists) toast.error("No school found with that U-DISE. You can still submit — Admin will reject if the school is missing.");
    } catch { setTransferContactReq((t) => ({ ...t, lookup: { exists: false } })); }
  };

  const submitTransferRequest = async () => {
    if (!transferContactReq) return;
    const target = (transferContactReq.target_udise || "").trim();
    const remark = (transferContactReq.remark || "").trim();
    if (!target) { toast.error("Target U-DISE is required"); return; }
    if (target === (active?.u_dise || "")) { toast.error("Target U-DISE must differ from current school"); return; }
    if (remark.length < 5) { toast.error("Remark must be at least 5 characters"); return; }
    setContactBusy(true);
    try {
      await api.post(`/clients/${active.id}/contacts/request-transfer`, {
        contact_number: transferContactReq.contact.number,
        contact_name: transferContactReq.contact.name,
        target_udise: target,
        remark,
      });
      toast.success("Transfer request submitted for admin approval");
      setTransferContactReq(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setContactBusy(false); }
  };

  const requestDocSupport = async () => {
    if (!active) return;
    if (!window.confirm(`Enable Documentation Support for "${active.client_name}"?\n\nThis will flag the school for the Documentation team and put it in their queue.`)) return;
    setContactBusy(true);
    try {
      const { data } = await api.post(`/crm/clients/${active.id}/enable-doc-support`, {});
      if (data?.already_enabled) {
        toast.success("Documentation Support is already enabled for this client");
      } else {
        toast.success("Documentation Support enabled — Doc team has been notified");
      }
      // Reflect the new flag in the active client + table so the button hides.
      setActive((c) => c ? { ...c, documentation_required: true } : c);
      setClients((arr) => arr.map((c) => c.id === active.id ? { ...c, documentation_required: true } : c));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setContactBusy(false); }
  };

  const openDetail = async (client) => {
    setDetail(client);
    setDetailExtra(null);
    try {
      const [reportsRes, extraRes] = await Promise.all([
        api.get(`/crm/clients/${client.id}/reports`),
        api.get(`/crm/clients/${client.id}/details`),
      ]);
      setDetailHistory(reportsRes.data || []);
      setDetailExtra(extraRes.data || null);
    } catch {
      setDetailHistory([]);
      setDetailExtra(null);
    }
  };

  const submit = async () => {
    if (!form.connection_method || !form.lead_status || !form.remark.trim()) {
      toast.error("Connection method, lead status and remark are required");
      return;
    }
    try {
      await api.post("/records/sales_crm_report", form);
      toast.success("Report logged");
      const done = active?.id;
      setForm({ ...emptyForm(), client_id: active.id, client_name: active.client_name, u_dise: active.u_dise || "" });
      const { data } = await api.get(`/crm/clients/${active.id}/reports`);
      setHistory(data);
      load();
      // Call-Through: after a successful log, jump to the next client automatically.
      if (callSession && done) advanceCallSession(done);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // ─── Call-Through dialer helpers ──────────────────────────────────
  // Advance to the next client in the queue. Called after a log submit.
  const advanceCallSession = (justDoneId) => {
    setCallSession((prev) => {
      if (!prev) return prev;
      const visited = new Set(prev.visited);
      if (justDoneId) visited.add(justDoneId);
      const remaining = prev.queue.filter((id) => !visited.has(id));
      if (!remaining.length) {
        toast.success(`Call-through complete — logged ${visited.size} client${visited.size === 1 ? "" : "s"}.`);
        setActive(null);
        return null;
      }
      const nextId = prev.mode === "random"
        ? remaining[Math.floor(Math.random() * remaining.length)]
        : remaining[0];
      const nextClient = clients.find((c) => c.id === nextId);
      if (nextClient) {
        // Open Log dialog for the next client
        setTimeout(() => openLog(nextClient), 100);
      }
      return { ...prev, current: nextClient, visited };
    });
  };

  // Stop the call-through session (from the sticky banner or Log-close).
  const stopCallSession = () => {
    if (!callSession) return;
    const done = callSession.visited.size;
    setCallSession(null);
    if (done > 0) toast.info(`Call-through stopped — ${done} logged.`);
  };
  return (
    <div className="max-w-[1500px] space-y-6">
      {/* Call-Through sticky banner */}
      {callSession && (
        <div className="sticky top-0 z-40 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-4 py-2 flex items-center gap-3 shadow-lg" data-testid="crmr-call-session-banner" data-crm-call-banner data-app-banner>
          <PhoneCall size={16} />
          <div className="text-[12px] font-semibold flex-1">
            Call-Through Active · {callSession.mode === "random" ? "Random" : "Sequential"} ·
            <span className="ml-1">Done {callSession.visited.size} / {callSession.queue.length}</span>
            {callSession.current && <span className="ml-2 opacity-80">Current: {callSession.current.client_name}</span>}
          </div>
          <button onClick={stopCallSession} className="h-7 px-3 rounded-full bg-white text-rose-700 text-[11.5px] font-bold hover:bg-rose-50 inline-flex items-center gap-1" data-testid="crmr-call-session-stop">
            <X size={11} /> Stop
          </button>
        </div>
      )}
      <PageHeader
        moduleKey="sales_crm_report"
        tone="sky"
        icon={PhoneCall}
        eyebrow="Sales Workflow"
        title="CRM Reporting"
        subtitle="Your assigned clients. Log every interaction — email, WhatsApp, call — and the resulting lead status."
        actions={
          <>
            <Button variant="outline" onClick={() => setMapOpen(true)} className="h-10" data-testid="crmr-map-existing">
              <Link2 size={14} className="mr-1.5" /> Request Existing School
            </Button>
            <Button onClick={() => setNewClientOpen(true)} className="h-10" data-testid="crmr-new-client">
              <Plus size={16} className="mr-1.5" /> Add New Client
            </Button>
          </>
        }
      />
      <Link to="/m/crm_clients" className="hidden">_</Link>

      <ClientFormDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        onSaved={load}
        title="New Client (Pending Approval)"
        submitLabel="Submit for Approval"
      />

      <MapExistingDialog open={mapOpen} onOpenChange={setMapOpen} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabAccessibleFor(user, "sales_crm_report", "clients") && (
            <TabsTrigger value="clients" data-testid="crmr-tab-clients">My Clients ({clients.length})</TabsTrigger>
          )}
          {tabAccessibleFor(user, "sales_crm_report", "reports") && (
            <TabsTrigger value="reports" data-testid="crmr-tab-reports">All My Reports ({reports.length})</TabsTrigger>
          )}
          {isAdmin && tabAccessibleFor(user, "sales_crm_report", "all-team") && <TabsTrigger value="all-team" data-testid="crmr-tab-all-team">All Team Reports</TabsTrigger>}
          {canMapping && tabAccessibleFor(user, "sales_crm_report", "mapping") && <TabsTrigger value="mapping" data-testid="crmr-tab-mapping">Mapping Requests ({mappingReqs.filter((m) => m.status === "Pending").length})</TabsTrigger>}
          {isAdmin && tabAccessibleFor(user, "sales_crm_report", "super-hot") && <TabsTrigger value="super-hot" data-testid="crmr-tab-super-hot">🔥 Super Hot Clients ({hotClients.length})</TabsTrigger>}
          {user?.role === "master_admin" && tabAccessibleFor(user, "sales_crm_report", "contact-approvals") && (
            <TabsTrigger value="contact-approvals" data-testid="crmr-tab-contact-approvals">
              <ShieldCheck size={12} className="mr-1.5" /> Contact Approvals
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="clients" className="mt-4 space-y-4">
          {/* All filters on a single line: Search · Client-Type dropdown · Stage chips · Follow-up chips · No-Log toggle */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="crmr-filter-strip">
            <div className="relative w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="pl-9 h-9" data-testid="crmr-search" />
            </div>

            {/* Client Type — dropdown (previously chips) */}
            {availableClientTypes.length > 0 && (
              <select
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e.target.value)}
                className="h-9 text-[12px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-2 min-w-[150px] focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                data-testid="crmr-type-select"
              >
                <option value="all">All Types ({clients.length})</option>
                {availableClientTypes.map((t) => {
                  const count = clients.filter((c) => {
                    const types = [c.client_type, ...(c.client_types || [])].filter(Boolean);
                    return types.includes(t);
                  }).length;
                  return <option key={t} value={t}>{t} ({count})</option>;
                })}
              </select>
            )}

            {/* Stage chips */}
            <CrmStageFilterChips
              value={stageFilter}
              onChange={setStageFilter}
              counts={stageCounts}
              testidPrefix="crmr-stage-chip"
              inline
            />

            {/* Follow-up chips */}
            {[
              { k: "all",       l: `All (${followupCounts.total})`,         active: "bg-slate-800 border-slate-800 text-white",   idle: "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" },
              { k: "overdue",   l: `Overdue (${followupCounts.overdue})`,   active: "bg-rose-600 border-rose-600 text-white",     idle: "bg-white border-rose-200 text-rose-700 hover:bg-rose-50" },
              { k: "today",     l: `Today (${followupCounts.today})`,       active: "bg-amber-600 border-amber-600 text-white",   idle: "bg-white border-amber-200 text-amber-700 hover:bg-amber-50" },
              { k: "upcoming",  l: `Upcoming (${followupCounts.upcoming})`, active: "bg-emerald-600 border-emerald-600 text-white", idle: "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50" },
            ].map(({ k, l, active, idle }) => (
              <button
                key={k}
                onClick={() => setFollowupFilter(k)}
                className={`h-8 px-3 text-[11.5px] font-semibold rounded-full border transition-all ${followupFilter === k ? active : idle}`}
                data-testid={`crmr-followup-chip-${k}`}
              >
                {l}
              </button>
            ))}

            {/* Clear all filters */}
            {(search || clientTypeFilter !== "all" || stageFilter !== "all" || followupFilter !== "all") && (
              <button
                onClick={() => {
                  setSearch("");
                  setClientTypeFilter("all");
                  setStageFilter("all");
                  setFollowupFilter("all");
                  setNoLogOnly(false);
                }}
                className="h-8 px-3 text-[11.5px] font-semibold rounded-full bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"
                data-testid="crmr-clear-filters"
              >
                <X size={11} /> Clear Filters
              </button>
            )}

            {/* Start Call-Through — sequential/random dialer */}
            <button
              onClick={() => setCallThroughOpen(true)}
              className="h-8 px-3 text-[11.5px] font-semibold rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white border border-transparent hover:from-indigo-700 hover:to-blue-700 inline-flex items-center gap-1 shadow-sm"
              data-testid="crmr-call-through-start"
              title="Guided calling — pick a filter and cycle through clients one by one"
            >
              <PhoneCall size={11} /> Start Calling
            </button>
          </div>
          {/* Card view — shows the same info as the table row but as a stacked card. */}
          {viewMode === "card" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="crmr-clients-cards">
              {filteredClients.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400 text-sm rounded-xl border border-dashed border-slate-200">
                  No clients are assigned to you yet.
                </div>
              )}
              {filteredClients.map((c) => {
                const primary = Array.isArray(c.contacts) ? c.contacts[0] : null;
                const lastLog = lastLogByClient.get(c.id) || lastLogByClient.get(c.u_dise);
                const lastContactName = lastLog?.contact_name || lastLog?.contact_person;
                const lastContactPhone = lastLog?.contact_phone;
                const when = lastLog?.created_at || lastLog?.submitted_at || "";
                const whenStr = when ? new Date(when).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "";
                const lead = lastLog?.lead_status || lastLog?.status || "";
                const leadCls = lead === "Positive" ? "bg-emerald-600 text-white" : lead === "Negative" ? "bg-rose-600 text-white" : lead ? "bg-slate-500 text-white" : "";
                return (
                  <div
                    key={c.id}
                    className="pk-card p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
                    data-testid={`crmr-client-card-${c.u_dise || c.id}`}
                  >
                    {/* Important-remark banner — same as the list view */}
                    {c.important_remark && (
                      <div className="-mx-4 -mt-4 px-4 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-[11px] font-semibold text-amber-900" data-testid={`crmr-card-important-${c.id}`}>
                        <span className="uppercase text-[9px] font-bold tracking-wider text-amber-700">Important</span>
                        <ClientImportantRemark remark={c.important_remark} />
                      </div>
                    )}
                    {/* Top row: brand mark + name + hot flags */}
                    <div className="flex items-start gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 text-white grid place-items-center shrink-0 shadow-sm ring-1 ring-indigo-300/40">
                        <Building2 size={16} strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                          <span className="truncate">{c.client_name}</span>
                          {c.is_hot && <span className="inline-flex items-center gap-0.5 bg-rose-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">🔥 HOT</span>}
                          {c.is_shared_lead && <span className="inline-flex items-center gap-0.5 bg-indigo-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">🤝 SHARED</span>}
                          <DocCycleBadge lifecycle={c.doc_lifecycle} />
                        </div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {c.u_dise && <span className="mono">{c.u_dise}</span>}
                          <Badge className={c.client_type?.startsWith("ATL") ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[9px]" : "bg-slate-100 text-slate-700 hover:bg-slate-100 text-[9px]"}>
                            {c.client_type || "—"}
                          </Badge>
                          {(c.district || c.state) && <span className="flex items-center gap-0.5"><MapPin size={9} />{[c.district, c.state].filter(Boolean).join(" · ")}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Stage row */}
                    <div className="flex flex-col gap-1.5 items-start">
                      <CrmStageBadge
                        client={c}
                        isMasterAdmin={user?.role === "master_admin"}
                        currentUserId={user?.id || ""}
                        onCleared={() => {
                          setClients((arr) => arr.map((x) => x.id === c.id
                            ? { ...x, crm_tag: null, crm_tag_by_id: null, crm_tag_by_name: null, crm_tag_at: null, crm_stage: deriveStage({ ...x, crm_tag: null }) }
                            : x));
                        }}
                      />
                      <CrmStageButtons
                        client={c}
                        onChanged={(tag) => {
                          const now = new Date().toISOString();
                          setClients((arr) => arr.map((x) => x.id === c.id
                            ? { ...x, crm_tag: tag, crm_tag_by_id: user?.id, crm_tag_by_name: user?.name || user?.email || "", crm_tag_at: now, crm_stage: tag }
                            : x));
                        }}
                      />
                    </div>

                    {/* Quote / GeM chips */}
                    {(statusFlags[c.id]?.quote || statusFlags[c.id]?.gem) && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {statusFlags[c.id]?.quote && (() => {
                          const q = statusFlags[c.id].quote;
                          const isApproved = q.stage === "Approved";
                          const hasLink = isApproved && q.final_quote_url;
                          const pill = (
                            <span className={"inline-flex items-center gap-0.5 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full " + (isApproved ? "bg-emerald-600" : "bg-amber-600")}>
                              QUOTE · {q.stage.toUpperCase()}{hasLink ? " ↗" : ""}
                            </span>
                          );
                          return hasLink ? <a href={q.final_quote_url} target="_blank" rel="noreferrer" className="hover:opacity-80">{pill}</a> : pill;
                        })()}
                        {statusFlags[c.id]?.gem && (
                          <span className="inline-flex items-center gap-0.5 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                            GEM · {statusFlags[c.id].gem.status.toUpperCase()}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Contact block */}
                    <div className="text-[12px] rounded-md bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800 p-2.5 space-y-1">
                      <div className="text-[9px] uppercase font-bold text-slate-500">Primary Contact</div>
                      {primary?.name ? (
                        <>
                          <div className="font-medium text-slate-800">
                            {primary.name}{primary.type ? <span className="text-[10px] text-slate-500 ml-1">· {primary.type}</span> : null}
                          </div>
                          {primary.phone && <div className="mono text-[11px] text-slate-600 flex items-center gap-1"><Phone size={9} /> {primary.phone}</div>}
                        </>
                      ) : (
                        <div className="text-slate-400 italic">—</div>
                      )}
                      <div className="mono text-[10.5px] text-slate-500 flex items-center gap-1 truncate">
                        <Mail size={9} /> {c.client_email || primary?.email || "—"}
                      </div>
                      {lastContactName && lastContactName !== primary?.name && (
                        <div className="mt-1.5 rounded bg-sky-50 border border-sky-100 px-1.5 py-1 text-[10px]" data-testid={`crmr-card-lastcontact-${c.id}`}>
                          <span className="uppercase font-bold text-[8.5px] text-sky-700">Last Contacted</span>
                          <div className="text-sky-900 font-medium">{lastContactName}</div>
                          {lastContactPhone && <div className="mono text-[9.5px] text-sky-700">{lastContactPhone}</div>}
                        </div>
                      )}
                    </div>

                    {/* Last-log strip — same info as list view */}
                    {lastLog && (
                      <div className="text-[10.5px] text-slate-600 flex items-center gap-2 flex-wrap rounded bg-slate-50/60 border border-slate-100 px-2 py-1.5" data-testid={`crmr-card-lastlog-${c.id}`}>
                        <span className="uppercase font-bold text-[9px] text-slate-500 tracking-wider">Last Log</span>
                        <span className="mono text-slate-400">{whenStr}</span>
                        {lead && <span className={`rounded-full text-[9px] font-bold px-1.5 py-0.5 ${leadCls}`}>{lead.toUpperCase()}</span>}
                        {lastLog.mode && <span className="rounded bg-slate-200 text-slate-700 text-[9px] font-semibold px-1.5 py-0.5 uppercase">{lastLog.mode}</span>}
                        {lastLog.notes && <span className="italic truncate max-w-[320px]">&ldquo;{lastLog.notes}&rdquo;</span>}
                        {lastLog.next_follow_up && <span className="rounded bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5">Next: {lastLog.next_follow_up}</span>}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap mt-auto">
                      {c.i_am_owner && (
                        <Button onClick={() => openCollab(c)} size="sm" variant="outline" className="h-8 text-xs border-violet-200 text-violet-700 hover:bg-violet-50">
                          <Users size={12} className="mr-1" /> Collaborate{(c.collaborators || []).length > 0 ? ` · ${c.collaborators.length}` : ""}
                        </Button>
                      )}
                      <Button onClick={() => openDetail(c)} size="sm" variant="outline" className="h-8 text-xs">
                        <Eye size={12} className="mr-1" /> Details
                      </Button>
                      <Button onClick={() => openLog(c)} size="sm" className="h-8 text-xs bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white ml-auto">
                        Log Report <ArrowUpRight size={11} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <th className="px-4 py-3 text-left pk-table-header">Client / Stage / U-DISE</th>
                  <th className="px-4 py-3 text-left pk-table-header">Type</th>
                  <th className="px-4 py-3 text-left pk-table-header">District</th>
                  <th className="px-4 py-3 text-left pk-table-header">State</th>
                  <th className="px-4 py-3 text-left pk-table-header">Primary Contact</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-400 text-sm">No clients are assigned to you yet.</td></tr>
                )}
                {filteredClients.flatMap((c) => {
                  const primary = Array.isArray(c.contacts) ? c.contacts[0] : null;
                  const lastLog = lastLogByClient.get(c.id) || lastLogByClient.get(c.u_dise);
                  const lastContactName = lastLog?.contact_name || lastLog?.contact_person;
                  const lastContactPhone = lastLog?.contact_phone;
                  const when = lastLog?.created_at || lastLog?.submitted_at || "";
                  const whenStr = when ? new Date(when).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "";
                  const lead = lastLog?.lead_status || lastLog?.status || "";
                  const leadCls = lead === "Positive" ? "bg-emerald-600 text-white" : lead === "Negative" ? "bg-rose-600 text-white" : lead ? "bg-slate-500 text-white" : "";
                  const key = c.u_dise || c.id;
                  return [
                    /* Important-remark banner row — spans all columns at the top */
                    c.important_remark && (
                      <tr key={`${key}-remark`} className="bg-amber-50" data-testid={`crmr-important-${c.id}`}>
                        <td colSpan={5} className="px-4 py-1.5 border-t border-amber-200">
                          <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-900">
                            <span className="uppercase text-[9px] font-bold tracking-wider text-amber-700">Important</span>
                            <ClientImportantRemark remark={c.important_remark} />
                          </div>
                        </td>
                      </tr>
                    ),
                    /* Main data row */
                    <tr
                      key={key}
                      className="border-b border-slate-100 hover:bg-slate-50/50 align-top"
                      data-testid={`crmr-client-${key}`}
                    >
                      {/* Col 1 — Client / Stage / U-DISE stacked */}
                      <td className="px-4 py-3 min-w-[240px]" data-label="Client">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-indigo-500 to-blue-600 text-white grid place-items-center shrink-0 shadow-sm ring-1 ring-indigo-300/40"><Building2 size={12} strokeWidth={2} /></div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900 truncate flex items-center gap-1 flex-wrap text-[13px]">
                              {c.client_name}
                              {c.is_hot && <span className="inline-flex items-center gap-0.5 bg-rose-600 text-white text-[8.5px] font-bold px-1 py-0.5 rounded-full">🔥 HOT</span>}
                              {c.is_shared_lead && <span className="inline-flex items-center gap-0.5 bg-indigo-600 text-white text-[8.5px] font-bold px-1 py-0.5 rounded-full">🤝 SHARED</span>}
                              <DocCycleBadge lifecycle={c.doc_lifecycle} />
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-start ml-9 mb-1">
                          <CrmStageBadge
                            client={c}
                            isMasterAdmin={user?.role === "master_admin"}
                            currentUserId={user?.id || ""}
                            onCleared={() => {
                              setClients((arr) => arr.map((x) => x.id === c.id
                                ? { ...x, crm_tag: null, crm_tag_by_id: null, crm_tag_by_name: null, crm_tag_at: null, crm_stage: deriveStage({ ...x, crm_tag: null }) }
                                : x));
                            }}
                          />
                        </div>
                        <div className="mono text-[11px] text-slate-600 ml-9">{c.u_dise || "—"}</div>
                        {(statusFlags[c.id]?.quote || statusFlags[c.id]?.gem) && (
                          <div className="mt-1 ml-9 flex items-center gap-1 flex-wrap">
                            {statusFlags[c.id]?.quote && (() => {
                              const q = statusFlags[c.id].quote;
                              const isApproved = q.stage === "Approved";
                              const hasLink = isApproved && q.final_quote_url;
                              const pill = (
                                <span className={"inline-flex items-center gap-0.5 text-white text-[8.5px] font-bold px-1 py-0.5 rounded-full " + (isApproved ? "bg-emerald-600" : "bg-amber-600")}>
                                  QUOTE · {q.stage.toUpperCase()}{hasLink ? " ↗" : ""}
                                </span>
                              );
                              return hasLink ? <a href={q.final_quote_url} target="_blank" rel="noreferrer">{pill}</a> : pill;
                            })()}
                            {statusFlags[c.id]?.gem && (
                              <span className="inline-flex items-center gap-0.5 bg-amber-500 text-white text-[8.5px] font-bold px-1 py-0.5 rounded-full">GEM · {statusFlags[c.id].gem.status.toUpperCase()}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3" data-label="Type">
                        <Badge className={c.client_type?.startsWith("ATL") ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]" : "bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]"}>
                          {c.client_type || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-[12px]" data-label="District">{c.district || "—"}</td>
                      <td className="px-4 py-3 text-[12px]" data-label="State">{c.state || "—"}</td>
                      <td className="px-4 py-3 text-[12px] min-w-[160px]" data-label="Primary Contact">
                        {primary?.name ? (
                          <>
                            <div className="font-medium text-slate-800 text-[12px]">{primary.name}</div>
                            {primary.phone && <div className="mono text-[10.5px] text-slate-500 flex items-center gap-1"><Phone size={9} /> {primary.phone}</div>}
                            {primary.type && <div className="text-[9.5px] text-slate-400">{primary.type}</div>}
                          </>
                        ) : <span className="text-slate-400 text-[11px]">—</span>}
                        {lastContactName && lastContactName !== primary?.name && (
                          <div className="mt-1.5 rounded bg-sky-50 border border-sky-100 px-1.5 py-0.5 text-[10px]">
                            <span className="uppercase font-bold text-[8.5px] text-sky-700">Last Contacted</span>
                            <div className="text-sky-900 font-medium">{lastContactName}</div>
                            {lastContactPhone && <div className="mono text-[9.5px] text-sky-700">{lastContactPhone}</div>}
                          </div>
                        )}
                      </td>
                    </tr>,
                    /* Action-strip row — Details / Log / Have-Funds + Doc-Support / GeM / Need Trainer / Quote / Collab */
                    <tr key={`${key}-actions`} className="bg-white border-b border-slate-100" data-testid={`crmr-actions-${key}`}>
                      <td colSpan={5} className="px-4 py-2">
                        <div className="flex items-center flex-wrap gap-1.5 justify-end">
                          {/* Positive / Negative — solid when active, outline when idle */}
                          <CrmStageButtons
                            client={c}
                            onChanged={(tag) => {
                              const now = new Date().toISOString();
                              setClients((arr) => arr.map((x) => x.id === c.id
                                ? { ...x, crm_tag: tag, crm_tag_by_id: user?.id, crm_tag_by_name: user?.name || user?.email || "", crm_tag_at: now, crm_stage: tag }
                                : x));
                            }}
                          />
                          <Button onClick={() => openDetail(c)} size="sm" variant="outline" className="h-7 text-[10.5px] px-2" data-testid={`crmr-details-${key}`}>
                            <Eye size={11} className="mr-0.5" /> Details
                          </Button>
                          {c.i_am_owner && (
                            <Button onClick={() => openCollab(c)} size="sm" variant="outline" className="h-7 text-[10.5px] px-2 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid={`crmr-collab-${key}`}>
                              <Users size={11} className="mr-0.5" /> Collaborate{(c.collaborators || []).length > 0 ? ` · ${c.collaborators.length}` : ""}
                            </Button>
                          )}
                          <Button onClick={() => openLog(c)} size="sm" className="h-7 text-[10.5px] px-2 bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid={`crmr-log-${key}`}>
                            Log Report <ArrowUpRight size={10} className="ml-0.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>,
                    /* Last-log strip row — spans all columns at the bottom of each client */
                    lastLog && (
                      <tr key={`${key}-log`} className="bg-slate-50/60 border-b-2 border-slate-200" data-testid={`crmr-lastlog-${key}`}>
                        <td colSpan={5} className="px-4 py-1.5">
                          <div className="text-[10.5px] text-slate-600 flex items-center gap-2 flex-wrap">
                            <span className="uppercase font-bold text-[9px] text-slate-500 tracking-wider">Last Log</span>
                            <span className="mono text-slate-400">{whenStr}</span>
                            {lead && <span className={`rounded-full text-[9px] font-bold px-1.5 py-0.5 ${leadCls}`}>{lead.toUpperCase()}</span>}
                            {lastLog.mode && <span className="rounded bg-slate-200 text-slate-700 text-[9px] font-semibold px-1.5 py-0.5 uppercase">{lastLog.mode}</span>}
                            {lastLog.notes && <span className="italic truncate max-w-[520px]">&ldquo;{lastLog.notes}&rdquo;</span>}
                            {lastLog.next_follow_up && <span className="rounded bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5">Next: {lastLog.next_follow_up}</span>}
                          </div>
                        </td>
                      </tr>
                    ),
                  ].filter(Boolean);
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative max-w-sm flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reports…" className="pl-9 h-10" data-testid="crmr-reports-search" />
            </div>
            <Select value={leadFilter} onValueChange={setLeadFilter}>
              <SelectTrigger className="h-10 w-44" data-testid="crmr-filter-lead"><SelectValue placeholder="Lead Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lead Status</SelectItem>
                {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Follow-up filter chips */}
          {(() => {
            const t = new Date().toISOString().slice(0, 10);
            const overdueCount = reports.filter((r) => r.next_follow_up && r.next_follow_up < t).length;
            const todayCount = reports.filter((r) => r.next_follow_up === t).length;
            const upcomingCount = reports.filter((r) => r.next_follow_up && r.next_follow_up > t).length;
            return (
              <Tabs value={followFilter} onValueChange={setFollowFilter} className="w-auto" data-testid="crmr-followup-chips">
                <TabsList>
                  <TabsTrigger value="all" data-testid="crmr-chip-all">All ({reports.length})</TabsTrigger>
                  <TabsTrigger value="overdue" data-testid="crmr-chip-overdue"><span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1" /> Overdue ({overdueCount})</TabsTrigger>
                  <TabsTrigger value="today" data-testid="crmr-chip-today"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1" /> Today ({todayCount})</TabsTrigger>
                  <TabsTrigger value="upcoming" data-testid="crmr-chip-upcoming"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1" /> Upcoming ({upcomingCount})</TabsTrigger>
                </TabsList>
              </Tabs>
            );
          })()}
          <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <th className="px-4 py-3 text-left pk-table-header">Client</th>
                  <th className="px-4 py-3 text-left pk-table-header">Connection</th>
                  <th className="px-4 py-3 text-left pk-table-header">Lead</th>
                  <th className="px-4 py-3 text-left pk-table-header">Next Follow-Up</th>
                  <th className="px-4 py-3 text-left pk-table-header">Remark</th>
                  <th className="px-4 py-3 text-left pk-table-header">Submitted At</th>
                  <th className="px-4 py-3 text-left pk-table-header">By</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No reports match.</td></tr>}
                {filteredReports.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`crmr-report-${r.id}`}>
                    <td className="px-4 py-3" data-label="Client">
                      <div className="font-semibold text-slate-900">{r.client_name}</div>
                      <div className="text-[10px] mono text-slate-500">{r.u_dise || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-xs" data-label="Connection">{r.connection_method}</td>
                    <td className="px-4 py-3" data-label="Lead"><Badge className={LEAD_COLOR[r.lead_status] || "bg-slate-100"}>{r.lead_status}</Badge></td>
                    <td className="px-4 py-3 mono text-xs" data-label="Next Follow-Up">{r.next_follow_up || "—"}</td>
                    <td className="px-4 py-3 text-xs max-w-md truncate" data-label="Remark">{r.remark}</td>
                    <td className="px-4 py-3 mono text-[11px] text-slate-600" data-label="Submitted At">{fmtDateTime(r.created_at)}</td>
                    <td className="px-4 py-3 text-[11px] text-slate-600" data-label="By">{r.created_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {viewMode === "card" && (
            filteredReports.length === 0 ? (
              <div className="pk-card p-12 text-center text-slate-400 text-sm">No reports match.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="crmr-reports-cards">
                {filteredReports.map((r) => (
                  <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`crmr-report-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{r.client_name}</div>
                        <div className="text-[10px] mono text-slate-500 truncate">{r.u_dise || ""}</div>
                      </div>
                      <Badge className={(LEAD_COLOR[r.lead_status] || "bg-slate-100") + " text-[10px] shrink-0"}>{r.lead_status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Connection</div><div className="text-slate-800">{r.connection_method || "—"}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Next Follow-Up</div><div className="mono text-slate-800">{r.next_follow_up || "—"}</div></div>
                      <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Remark</div><div className="text-slate-700 line-clamp-3">{r.remark || "—"}</div></div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10.5px] text-slate-500">
                      <span className="mono">{fmtDateTime(r.created_at)}</span>
                      <span className="truncate max-w-[50%] text-right">{r.created_by_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="all-team" className="mt-4 space-y-4">
            {/* v109.9.92 — Date range filter + PDF export */}
            <div className="pk-card p-4 border-l-4 border-indigo-500 space-y-3" data-testid="crmr-date-filter-panel">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-indigo-600" />
                <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-indigo-700">Filter submissions by date</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Tabs value={dateMode} onValueChange={setDateMode} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="all" data-testid="crmr-date-mode-all">All time</TabsTrigger>
                    <TabsTrigger value="day" data-testid="crmr-date-mode-day">Selected Date</TabsTrigger>
                    <TabsTrigger value="month" data-testid="crmr-date-mode-month">Selected Month</TabsTrigger>
                    <TabsTrigger value="range" data-testid="crmr-date-mode-range">Custom Range</TabsTrigger>
                  </TabsList>
                </Tabs>
                {dateMode === "day" && (
                  <Input
                    type="date"
                    value={dateDay}
                    onChange={(e) => setDateDay(e.target.value)}
                    className="h-10 w-44"
                    data-testid="crmr-date-day"
                  />
                )}
                {dateMode === "month" && (
                  <Input
                    type="month"
                    value={dateMonth}
                    onChange={(e) => setDateMonth(e.target.value)}
                    className="h-10 w-44"
                    data-testid="crmr-date-month"
                  />
                )}
                {dateMode === "range" && (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="date"
                      value={dateStart}
                      onChange={(e) => setDateStart(e.target.value)}
                      className="h-10 w-40"
                      data-testid="crmr-date-start"
                    />
                    <span className="text-slate-400 text-[12px]">→</span>
                    <Input
                      type="date"
                      value={dateEnd}
                      onChange={(e) => setDateEnd(e.target.value)}
                      className="h-10 w-40"
                      data-testid="crmr-date-end"
                    />
                  </div>
                )}
                <div className="flex-1" />
                <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-indigo-50/60" data-testid="crmr-date-match-count">
                  {filteredAll.length} log{filteredAll.length === 1 ? "" : "s"} match
                </Badge>
                <Button
                  onClick={downloadAllReportsPdf}
                  disabled={downloadingPdf}
                  className="h-10 bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="crmr-download-pdf"
                >
                  <Download size={14} className="mr-1.5" />
                  {downloadingPdf ? "Preparing…" : "Download PDF"}
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative max-w-sm flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search across team reports…" className="pl-9 h-10" data-testid="crmr-all-search" />
              </div>
              <Select value={submitterFilter} onValueChange={setSubmitterFilter}>
                <SelectTrigger className="h-10 w-56" data-testid="crmr-filter-submitter"><SelectValue placeholder="Submitter" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Submitters</SelectItem>
                  {submitterOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={leadFilter} onValueChange={setLeadFilter}>
                <SelectTrigger className="h-10 w-44" data-testid="crmr-filter-all-lead"><SelectValue placeholder="Lead Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Lead Status</SelectItem>
                  {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={loadAllReports} className="h-10" data-testid="crmr-refresh-all">Refresh</Button>
            </div>

            {/* v109.9.93 — Unified "View" segmented control replaces the two
                confusing chip rows. All counts are contextual to the current
                search / date / submitter / lead narrowing. */}
            <div className="pk-card p-3 flex items-center gap-3 flex-wrap" data-testid="crmr-allteam-view">
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 shrink-0">View</div>
              <Tabs value={allTeamView} onValueChange={setAllTeamView} className="w-auto flex-1 min-w-0">
                <TabsList className="flex-wrap h-auto gap-1">
                  <TabsTrigger value="all" data-testid="crmr-view-all">All Assigned <span className="ml-1.5 tabular-nums text-[11px] opacity-70">({allTeamViewCounts.all})</span></TabsTrigger>
                  <TabsTrigger value="with_logs" data-testid="crmr-view-with_logs">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
                    With Logs <span className="ml-1.5 tabular-nums text-[11px] opacity-70">({allTeamViewCounts.with_logs})</span>
                  </TabsTrigger>
                  <TabsTrigger value="no_log" data-testid="crmr-view-no_log">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5" />
                    No Log <span className="ml-1.5 tabular-nums text-[11px] opacity-70">({allTeamViewCounts.no_log})</span>
                  </TabsTrigger>
                  <span className="w-px h-5 bg-slate-200 mx-1 self-center" aria-hidden="true" />
                  <TabsTrigger value="fup_missed" data-testid="crmr-view-fup_missed">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5" />
                    Follow-up Missed <span className="ml-1.5 tabular-nums text-[11px] opacity-70">({allTeamViewCounts.fup_missed})</span>
                  </TabsTrigger>
                  <TabsTrigger value="fup_today" data-testid="crmr-view-fup_today">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5" />
                    Due Today <span className="ml-1.5 tabular-nums text-[11px] opacity-70">({allTeamViewCounts.fup_today})</span>
                  </TabsTrigger>
                  <TabsTrigger value="fup_upcoming" data-testid="crmr-view-fup_upcoming">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1.5" />
                    Upcoming <span className="ml-1.5 tabular-nums text-[11px] opacity-70">({allTeamViewCounts.fup_upcoming})</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Active-filter pills — visible only when any non-default filter
                is applied. Each pill clears just its own axis. */}
            {(() => {
              const chips = [];
              if (submitterFilter !== "all") {
                const s = submitterOptions.find((o) => o.id === submitterFilter);
                chips.push({ key: "sub", label: `Submitter: ${s?.name || submitterFilter}`, clear: () => setSubmitterFilter("all") });
              }
              if (leadFilter !== "all") chips.push({ key: "lead", label: `Lead: ${leadFilter}`, clear: () => setLeadFilter("all") });
              if (dateMode !== "all") {
                const stamp = dateMode === "day" ? dateDay
                  : dateMode === "month" ? dateMonth
                  : `${dateStart} → ${dateEnd}`;
                chips.push({ key: "date", label: `Date: ${stamp}`, clear: () => setDateMode("all") });
              }
              if (allTeamView !== "all") {
                const map = { with_logs: "With Logs", no_log: "No Log", fup_missed: "Follow-up Missed", fup_today: "Due Today", fup_upcoming: "Upcoming" };
                chips.push({ key: "view", label: `View: ${map[allTeamView] || allTeamView}`, clear: () => setAllTeamView("all") });
              }
              if (search) chips.push({ key: "search", label: `Search: "${search}"`, clear: () => setSearch("") });
              if (chips.length === 0) return null;
              return (
                <div className="flex items-center gap-2 flex-wrap" data-testid="crmr-active-filters">
                  <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-500">Active</span>
                  {chips.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={c.clear}
                      className="group inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300 text-[11.5px] font-semibold pl-2.5 pr-1.5 py-1 transition-colors"
                      data-testid={`crmr-filter-chip-${c.key}`}
                    >
                      {c.label}
                      <span className="w-4 h-4 grid place-items-center rounded-full bg-white/70 group-hover:bg-white text-indigo-600">
                        <X size={10} strokeWidth={2.6} />
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitterFilter("all");
                      setLeadFilter("all");
                      setDateMode("all");
                      setAllTeamView("all");
                      setSearch("");
                    }}
                    className="text-[11px] font-bold text-slate-500 hover:text-rose-600 underline underline-offset-2 ml-1"
                    data-testid="crmr-clear-all-filters"
                  >
                    Clear all
                  </button>
                </div>
              );
            })()}

            {groupedAll.length === 0 && (
              <div className="pk-card p-12 text-center text-sm text-slate-400" data-testid="crmr-allteam-empty">No clients match the filters.</div>
            )}

            {groupedByEmployee.map((e) => {
              const empOpen = !collapsedEmp[e.employee_id];
              return (
                <div key={e.employee_id} className="pk-card overflow-hidden border-l-4 border-indigo-500" data-testid={`crmr-emp-card-${e.employee_id}`}>
                  <button
                    onClick={() => setCollapsedEmp((s) => ({ ...s, [e.employee_id]: !s[e.employee_id] }))}
                    className="w-full flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-indigo-50 via-white to-blue-50/60 border-b border-indigo-100 text-slate-900 hover:from-indigo-100 hover:to-blue-100/60 transition-colors"
                    data-testid={`crmr-emp-toggle-${e.employee_id}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center shadow-sm ring-1 ring-indigo-300/40">
                        {empOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      </div>
                      <span className="font-bold text-[15px] tracking-tight text-indigo-900">{e.employee_name}</span>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-indigo-200 text-indigo-700 bg-white/70">
                        {e.clients.length} school{e.clients.length === 1 ? "" : "s"}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-indigo-200 text-indigo-700 bg-white/70">
                        {e.total_logs} log{e.total_logs === 1 ? "" : "s"}
                      </Badge>
                      {e.no_log_count > 0 && (
                        <Badge className="bg-rose-600 text-white border-rose-700 text-[10px]" data-testid={`crmr-emp-nolog-${e.employee_id}`}>
                          <Inbox size={10} className="mr-1" /> {e.no_log_count} no log
                        </Badge>
                      )}
                      {e.missed_count > 0 && (
                        <Badge className="bg-amber-600 text-white border-amber-700 text-[10px]" data-testid={`crmr-emp-missed-${e.employee_id}`}>
                          ⚠ {e.missed_count} missed
                        </Badge>
                      )}
                    </div>
                  </button>

                  {empOpen && (
                    <div className="divide-y divide-slate-100">
                      {e.clients.map((b) => {
                        const c = b.client;
                        const isOpen = !collapsed[c.id];
                        return (
                          <div key={c.id} className={`${b.no_log ? "bg-rose-50/30" : b.followup_missed ? "bg-amber-50/30" : ""}`} data-testid={`crmr-client-card-${c.id}`}>
                            <div className="flex items-center justify-between px-6 py-2.5 hover:bg-slate-50/60 transition-colors gap-2">
                              <button
                                onClick={() => setCollapsed((s) => ({ ...s, [c.id]: !s[c.id] }))}
                                className="flex-1 flex items-center gap-2 flex-wrap text-left"
                                data-testid={`crmr-client-toggle-${c.id}`}
                              >
                                {isOpen ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
                                <span className="font-semibold text-slate-900 text-sm">{c.client_name}</span>
                                {c.u_dise && <span className="text-[10px] mono text-slate-500">{c.u_dise}</span>}
                                <Badge variant="secondary" className="text-[10px]">{b.logs.length} log{b.logs.length === 1 ? "" : "s"}</Badge>
                                {b.no_log && <Badge className="bg-rose-100 text-rose-800 text-[10px]" data-testid={`crmr-tag-no-log-${c.id}`}>📭 No Log Submitted</Badge>}
                                {b.followup_missed && <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`crmr-tag-missed-${c.id}`}>⚠ Follow-up Missed</Badge>}
                              </button>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[10.5px] text-slate-500 mono">
                                  {b.latest ? `Last: ${fmtDateTime(b.latest.created_at)}` : "Never"}
                                </span>
                                <LeadCallRequestButton
                                  clientId={c.id}
                                  clientName={c.client_name}
                                  assignedToName={e.employee_name}
                                  source="crm_reporting_all_team"
                                />
                              </div>
                            </div>
                            {isOpen && (
                              <div className="overflow-x-auto bg-slate-50/40 border-t border-slate-100">
                                {c.important_remark && (
                                  <div className="px-5 py-2">
                                    <ClientImportantRemark remark={c.important_remark} />
                                  </div>
                                )}
                                {b.logs.length === 0 ? (
                                  <div className="p-5 text-center text-[12px] text-rose-700">
                                    No reporting logs have been submitted for this school yet.
                                  </div>
                                ) : (
                                  <table className="w-full text-sm responsive-table">
                                    <thead className="border-b border-slate-100">
                                      <tr>
                                        <th className="px-6 py-2 text-left pk-table-header">Submitted By</th>
                                        <th className="px-4 py-2 text-left pk-table-header">Connection</th>
                                        <th className="px-4 py-2 text-left pk-table-header">Lead</th>
                                        <th className="px-4 py-2 text-left pk-table-header">Next Follow-Up</th>
                                        <th className="px-4 py-2 text-left pk-table-header">Remark</th>
                                        <th className="px-4 py-2 text-left pk-table-header">Submitted At</th>
                                        {user?.role === "master_admin" && <th className="px-4 py-2 text-right pk-table-header w-20">Action</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {b.logs.map((r) => (
                                        <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-white" data-testid={`crmr-all-row-${r.id}`}>
                                          <td className="px-6 py-2.5 text-xs font-medium" data-label="Submitted By">{r.created_by_name || "—"}</td>
                                          <td className="px-4 py-2.5 text-xs" data-label="Connection">{r.connection_method}</td>
                                          <td className="px-4 py-2.5" data-label="Lead"><Badge className={LEAD_COLOR[r.lead_status] || "bg-slate-100"}>{r.lead_status}</Badge></td>
                                          <td className="px-4 py-2.5 mono text-xs" data-label="Next Follow-Up">{r.next_follow_up || "—"}</td>
                                          <td className="px-4 py-2.5 text-xs max-w-md truncate" title={r.remark} data-label="Remark">{r.remark}</td>
                                          <td className="px-4 py-2.5 mono text-[11px] text-slate-600" data-label="Submitted At">{fmtDateTime(r.created_at)}</td>
                                          {user?.role === "master_admin" && (
                                            <td className="px-4 py-2.5 text-right" data-label="Action">
                                              <button
                                                type="button"
                                                onClick={async () => {
                                                  if (!window.confirm(`Delete this reporting log by ${r.created_by_name}? This is a hard delete and cannot be undone.`)) return;
                                                  try {
                                                    await api.delete(`/records/sales_crm_report/${r.id}`);
                                                    toast.success("Log deleted");
                                                    loadAllReports();
                                                  } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                                                }}
                                                className="h-6 px-2 rounded-md text-[10.5px] font-semibold text-rose-700 border border-rose-200 hover:bg-rose-50 inline-flex items-center gap-1"
                                                title="Delete log (Master Admin only)"
                                                data-testid={`crmr-all-delete-${r.id}`}
                                              >
                                                <Trash2 size={11} /> Delete
                                              </button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="mapping" className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">CRM Manager / Master Admin</div>
                <h2 className="heading text-xl font-bold text-slate-900">Mapping Requests from CRM Reporting</h2>
                <p className="text-xs text-slate-500 mt-0.5">Approve or reject requests from sales associates to be mapped to an existing school.</p>
              </div>
              <Button size="sm" variant="outline" onClick={loadMappingReqs} className="h-9" data-testid="crmr-mapping-refresh">Refresh</Button>
            </div>
            <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
              <table className="w-full text-sm responsive-table">
                <thead className="border-b border-slate-200 bg-slate-50/60">
                  <tr>
                    <th className="px-4 py-3 text-left pk-table-header">Client / School</th>
                    <th className="px-4 py-3 text-left pk-table-header">U-DISE</th>
                    <th className="px-4 py-3 text-left pk-table-header">Currently Assigned</th>
                    <th className="px-4 py-3 text-left pk-table-header">Requested By</th>
                    <th className="px-4 py-3 text-left pk-table-header">Reason</th>
                    <th className="px-4 py-3 text-left pk-table-header">Requested</th>
                    <th className="px-4 py-3 text-left pk-table-header">Status</th>
                    <th className="px-4 py-3 text-right pk-table-header">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mappingReqs.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-12 text-slate-400 text-sm">No mapping requests yet.</td></tr>
                  )}
                  {mappingReqs.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`crmr-mapping-${m.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900" data-label="Client">{m.client_name}</td>
                      <td className="px-4 py-3 mono text-[12px]" data-label="U-DISE">{m.u_dise || "—"}</td>
                      <td className="px-4 py-3 text-[12px]" data-label="Currently Assigned">{m.current_assignee_name || <span className="text-slate-400 italic">unassigned</span>}</td>
                      <td className="px-4 py-3 text-[12px] font-medium" data-label="Requested By">{m.requested_by_name}</td>
                      <td className="px-4 py-3 text-xs max-w-sm truncate" title={m.reason} data-label="Reason">{m.reason || "—"}</td>
                      <td className="px-4 py-3 mono text-[11px] text-slate-600" data-label="Requested">{fmtDateTime(m.created_at)}</td>
                      <td className="px-4 py-3" data-label="Status">
                        <Badge className={
                          m.status === "Approved" ? "bg-green-100 text-green-700 hover:bg-green-100 text-[10px]" :
                          m.status === "Rejected" ? "bg-red-100 text-red-700 hover:bg-red-100 text-[10px]" :
                          "bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]"
                        }>{m.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right" data-label="Action">
                        {m.status === "Pending" ? (
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" onClick={() => decideMapping(m.id, true)} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`crmr-mapping-approve-${m.id}`}>Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => decideMapping(m.id, false)} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`crmr-mapping-reject-${m.id}`}>Reject</Button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-500">by {m.decided_by || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {viewMode === "card" && (
              mappingReqs.length === 0 ? (
                <div className="pk-card p-12 text-center text-slate-400 text-sm">No mapping requests yet.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="crmr-mapping-cards">
                  {mappingReqs.map((m) => (
                    <div key={m.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`crmr-mapping-card-${m.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 truncate">{m.client_name}</div>
                          <div className="text-[10px] mono text-slate-500 truncate">U-DISE {m.u_dise || "—"}</div>
                        </div>
                        <Badge className={(m.status === "Approved" ? "bg-green-100 text-green-700" : m.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800") + " text-[10px] shrink-0"}>{m.status}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Current</div><div className="text-slate-800 truncate">{m.current_assignee_name || <span className="text-slate-400 italic">unassigned</span>}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Requested By</div><div className="text-slate-800 truncate">{m.requested_by_name}</div></div>
                        <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Reason</div><div className="text-slate-700 line-clamp-2" title={m.reason}>{m.reason || "—"}</div></div>
                        <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Requested</div><div className="mono text-[11px] text-slate-600">{fmtDateTime(m.created_at)}</div></div>
                      </div>
                      <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-1.5">
                        {m.status === "Pending" ? (
                          <>
                            <Button size="sm" onClick={() => decideMapping(m.id, true)} className="h-7 px-2 text-[10.5px] bg-green-600 hover:bg-green-700 text-white" data-testid={`crmr-mapping-approve-card-${m.id}`}>Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => decideMapping(m.id, false)} className="h-7 px-2 text-[10.5px] text-red-600 border-red-200 hover:bg-red-50" data-testid={`crmr-mapping-reject-card-${m.id}`}>Reject</Button>
                          </>
                        ) : (
                          <span className="text-[10.5px] text-slate-500">Decided by {m.decided_by || "—"}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="super-hot" className="mt-4 space-y-3">
            <div className="pk-card p-4 bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🔥</span>
                <div>
                  <div className="heading text-lg font-bold text-rose-800">Super Hot Clients</div>
                  <div className="text-[11px] text-slate-600">Every school flagged with funds in account — auto-detected from ATL 1.0 fund-credited or ATL 2.0 application-approved.</div>
                </div>
              </div>
            </div>
            <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
              <table className="w-full text-sm responsive-table">
                <thead className="border-b border-slate-200 bg-slate-50/60">
                  <tr>
                    <th className="px-4 py-3 text-left pk-table-header">Client</th>
                    <th className="px-4 py-3 text-left pk-table-header">U-DISE</th>
                    <th className="px-4 py-3 text-left pk-table-header">Type</th>
                    <th className="px-4 py-3 text-left pk-table-header">Reason</th>
                    <th className="px-4 py-3 text-left pk-table-header">Sales Owner</th>
                    <th className="px-4 py-3 text-left pk-table-header">Doc Team</th>
                    <th className="px-4 py-3 text-right pk-table-header">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hotClients.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No hot clients yet.</td></tr>}
                  {hotClients.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100" data-testid={`hot-${c.u_dise || c.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900" data-label="Client">
                        <span className="inline-flex items-center gap-1.5">🔥 {c.client_name}</span>
                      </td>
                      <td className="px-4 py-3 mono text-[12px]" data-label="U-DISE">{c.u_dise || "—"}</td>
                      <td className="px-4 py-3" data-label="Type"><Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">{c.client_type || "—"}</Badge></td>
                      <td className="px-4 py-3 text-[12px] text-slate-700" data-label="Reason">{c.hot_reason || "Has funds"}</td>
                      <td className="px-4 py-3 text-[12px]" data-label="Sales Owner">{c.assigned_to_name || <span className="text-slate-400">— unassigned</span>}</td>
                      <td className="px-4 py-3 text-[12px]" data-label="Doc Team">{(c.documentation_assignee_names || []).join(", ") || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-right" data-label="Actions">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <Button onClick={() => openDetail(c)} size="sm" variant="outline" className="h-8 text-xs" data-testid={`hot-view-${c.u_dise || c.id}`}>
                            <Eye size={11} className="mr-1" /> Details
                          </Button>
                          <Select value="" onValueChange={(v) => assignSalesOwner(c, v)}>
                            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`hot-assign-${c.u_dise || c.id}`}>
                              <SelectValue placeholder={c.assigned_to_name ? "Reassign…" : "Assign…"} />
                            </SelectTrigger>
                            <SelectContent>
                              {salesEligible.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name} {u.employee_code ? `· ${u.employee_code}` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {viewMode === "card" && (
              hotClients.length === 0 ? (
                <div className="pk-card p-12 text-center text-slate-400 text-sm">No hot clients yet.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="crmr-hot-cards">
                  {hotClients.map((c) => (
                    <div key={c.id} className="pk-card p-4 hover:shadow-md transition-shadow border-l-4 border-rose-400" data-testid={`hot-card-${c.u_dise || c.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 truncate flex items-center gap-1.5">🔥 {c.client_name}</div>
                          <div className="text-[10px] mono text-slate-500 truncate">U-DISE {c.u_dise || "—"}</div>
                        </div>
                        <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] shrink-0">{c.client_type || "—"}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                        <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Reason</div><div className="text-slate-700">{c.hot_reason || "Has funds"}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Sales Owner</div><div className="text-slate-800 truncate">{c.assigned_to_name || <span className="text-slate-400">— unassigned</span>}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Doc Team</div><div className="text-slate-800 truncate">{(c.documentation_assignee_names || []).join(", ") || <span className="text-slate-400">—</span>}</div></div>
                      </div>
                      <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-1.5">
                        <Button onClick={() => openDetail(c)} size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" data-testid={`hot-view-card-${c.u_dise || c.id}`}>
                          <Eye size={11} className="mr-1" /> Details
                        </Button>
                        <Select value="" onValueChange={(v) => assignSalesOwner(c, v)}>
                          <SelectTrigger className="h-7 w-36 text-[10.5px]" data-testid={`hot-assign-card-${c.u_dise || c.id}`}>
                            <SelectValue placeholder={c.assigned_to_name ? "Reassign…" : "Assign…"} />
                          </SelectTrigger>
                          <SelectContent>
                            {salesEligible.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name} {u.employee_code ? `· ${u.employee_code}` : ""}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </TabsContent>
        )}
        {user?.role === "master_admin" && (
          <TabsContent value="contact-approvals" className="mt-4">
            <ContactDeleteRequests embedded />
          </TabsContent>
        )}
      </Tabs>

      {/* Log Report Dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setHistory([]); setLogContacts([]); setAddContactOpen(false); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading text-lg flex items-center gap-2 flex-wrap">
              Log Report — {active?.client_name}
              {active?.u_dise && <span className="mono text-[11px] font-normal text-slate-500">· U-DISE {active.u_dise}</span>}
              {active?.client_type && <Badge className={active.client_type?.startsWith("ATL") ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]" : "bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]"}>{active.client_type}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {/* Contacts pane — select who you connected with, add new, or
              request delete / transfer of an existing contact. */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="crmr-log-contacts">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Connected With · {logContacts.length} contact(s)</div>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAddContactOpen((s) => !s)} data-testid="crmr-log-add-contact-toggle">
                <Plus size={12} className="mr-1" /> {addContactOpen ? "Cancel" : "Add Contact"}
              </Button>
            </div>
            {addContactOpen && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 bg-white border border-slate-200 rounded-md p-2.5" data-testid="crmr-log-add-contact-form">
                <Input placeholder="Name" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className="h-9 text-[12px]" data-testid="crmr-log-new-contact-name" />
                <Input placeholder="Phone" value={newContact.number} onChange={(e) => setNewContact({ ...newContact, number: e.target.value })} className="h-9 text-[12px]" data-testid="crmr-log-new-contact-number" />
                <Input placeholder="Email (optional)" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className="h-9 text-[12px]" data-testid="crmr-log-new-contact-email" />
                <div className="flex gap-1">
                  <Input placeholder="Role (e.g. Principal)" value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} className="h-9 text-[12px] flex-1" data-testid="crmr-log-new-contact-role" />
                  <Button size="sm" disabled={contactBusy} onClick={submitAddContact} className="h-9 text-[11px] bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="crmr-log-new-contact-save">Save</Button>
                </div>
              </div>
            )}
            {logContacts.length === 0 ? (
              <div className="text-[11.5px] italic text-slate-500 py-1">No contacts saved for this school yet. Add one above to attribute this log.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {logContacts.map((co) => {
                  const selected = (form.connected_contact_number || "") === co.number;
                  return (
                    <div
                      key={co.number || co.name}
                      className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-[12px] ${selected ? "bg-indigo-50 border-indigo-300" : "bg-white border-slate-200"}`}
                      data-testid={`crmr-log-contact-row-${(co.number || co.name).replace(/[^A-Za-z0-9]/g, "_")}`}
                    >
                      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                        <input
                          type="radio"
                          name="connected_contact"
                          checked={selected}
                          onChange={() => setForm({ ...form, connected_contact_number: co.number, connected_contact_name: co.name || "" })}
                          className="accent-indigo-600"
                          data-testid={`crmr-log-contact-pick-${(co.number || co.name).replace(/[^A-Za-z0-9]/g, "_")}`}
                        />
                        <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
                          <span className="font-semibold text-slate-800 truncate">{co.name || <em className="text-slate-400">(no name)</em>}</span>
                          {co.role && <span className="text-[10px] text-slate-500">· {co.role}</span>}
                          <span className="mono text-[11px] text-slate-600">{co.number || "—"}</span>
                          {co.is_primary && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[9px]">Primary</Badge>}
                        </div>
                      </label>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[10.5px] text-amber-700 border-amber-200 hover:bg-amber-50"
                          onClick={() => setTransferContactReq({ contact: co, target_udise: "", remark: "", lookup: null })}
                          data-testid={`crmr-log-contact-transfer-${(co.number || co.name).replace(/[^A-Za-z0-9]/g, "_")}`}
                        >
                          Transfer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[10.5px] text-rose-700 border-rose-200 hover:bg-rose-50"
                          onClick={() => setDeleteContactReq({ contact: co, remark: "" })}
                          data-testid={`crmr-log-contact-delete-${(co.number || co.name).replace(/[^A-Za-z0-9]/g, "_")}`}
                        >
                          Request Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-3">
            <F label="Connection Method *">
              <Select value={form.connection_method} onValueChange={(v) => setForm({ ...form, connection_method: v })}>
                <SelectTrigger data-testid="crmr-method"><SelectValue placeholder="How did you connect?" /></SelectTrigger>
                <SelectContent>{CONNECT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Lead Status *">
              <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}>
                <SelectTrigger data-testid="crmr-lead"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Next Follow-Up Date">
              <Input type="date" value={form.next_follow_up} onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })} data-testid="crmr-follow" />
            </F>
            <div className="md:col-span-3">
              <F label="Remark / Discussion Notes *">
                <Textarea rows={4} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} placeholder="Capture the key takeaways of this interaction — decision-makers spoken to, products discussed, objections raised, next-step commitments…" data-testid="crmr-remark" />
              </F>
            </div>
          </div>
          <div className="mt-2 mb-2 flex items-center justify-between">
            <div className="pk-table-header">Activity History · {history.length}</div>
            {history.length > 4 && <span className="text-[10px] text-slate-400">Scroll for older entries</span>}
          </div>
          <div className="max-h-72 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-slate-100 pt-3 pr-1">
            {history.length === 0 ? (
              <div className="md:col-span-2 text-xs text-slate-400 italic text-center py-6">No previous reports for this client.</div>
            ) : history.map((h) => (
              <div key={h.id} className="border border-slate-200 rounded-md p-2.5 text-[12px] bg-white" data-testid={`crmr-history-${h.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Badge className={LEAD_COLOR[h.lead_status] || "bg-slate-100"}>{h.lead_status}</Badge>
                    <span className="text-slate-500">via {h.connection_method}</span>
                  </div>
                  <span className="mono text-[10px] text-slate-400">{fmtDateTime(h.created_at)}</span>
                </div>
                <div className="text-slate-700 mt-1 line-clamp-3">{h.remark}</div>
                <div className="flex items-center justify-between mt-1">
                  {h.next_follow_up && <div className="text-[10px] mono text-slate-500">Next: {h.next_follow_up}</div>}
                  <div className="text-[10px] text-slate-500 ml-auto">By <span className="font-semibold">{h.created_by_name || "—"}</span></div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="!flex-nowrap gap-1.5 overflow-x-auto items-center">
            {/* Have Funds — RED */}
            {active && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFundsClient({ client: active, amount: active.funds_amount || "", is_hot: !!active.is_hot })}
                className={`h-8 text-[11px] px-2.5 shrink-0 bg-rose-600 border-rose-600 text-white hover:bg-rose-700 ${active.has_funds ? "ring-2 ring-rose-300" : ""}`}
                data-testid="crmr-log-have-funds"
              >
                💰 {active.has_funds ? "Has Funds" : "Have Funds"}
              </Button>
            )}
            {(() => {
              if (!active) return null;
              const types = [active.client_type, ...(active.client_types || [])].filter(Boolean);
              const isAtl = types.some((t) => /ATL\s*(1|2)\.0/i.test(String(t)));
              if (!isAtl) return null;
              if (active.documentation_required) {
                return (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1 shrink-0" data-testid="crmr-doc-support-enabled-badge">
                    <FileText size={11} /> Doc ✓
                  </span>
                );
              }
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={requestDocSupport}
                  disabled={contactBusy}
                  className="h-8 text-[11px] px-2.5 shrink-0 border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="crmr-request-doc-support"
                >
                  <FileText size={12} className="mr-1" /> Doc Support
                </Button>
              );
            })()}
            <Button variant="outline" size="sm" onClick={() => setGemOpen(true)} className="h-8 text-[11px] px-2.5 shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="crmr-gem-support">
              <Store size={12} className="mr-1" /> GeM
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!trainersList.length) {
                  try {
                    const [tr, emp] = await Promise.all([
                      api.get("/trainers"),
                      api.get("/users", { params: { active: true } }).catch(() => ({ data: [] })),
                    ]);
                    setTrainersList(tr.data || []);
                    setActiveEmployees(emp.data || []);
                  } catch { /* ignore */ }
                }
                setNeedTrainerForm({
                  ...emptyReqForm(),
                  client_id: active?.id || "",
                  client_name: active?.client_name || "",
                  assigned_by_id: user?.id || "",
                  assigned_by_name: user?.name || user?.email || "",
                  source: "crm_log_report",
                  source_ref: active?.id || "",
                });
                setNeedTrainerOpen(true);
              }}
              className="h-8 text-[11px] px-2.5 shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="crmr-need-trainer"
            >
              <GraduationCap size={12} className="mr-1" /> Trainer
            </Button>
            <Button variant="outline" size="sm" onClick={() => setQuoteOpen(true)} className="h-8 text-[11px] px-2.5 shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="crmr-request-quote">
              <FileText size={12} className="mr-1" /> Quote
            </Button>
            {active?.i_am_owner && (
              <Button variant="outline" size="sm" onClick={() => openCollab(active)} className="h-8 text-[11px] px-2.5 shrink-0 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="crmr-log-collab">
                <Users size={12} className="mr-1" /> Collab{(active.collaborators || []).length > 0 ? ` · ${active.collaborators.length}` : ""}
              </Button>
            )}
            <div className="flex-1 min-w-[8px]" />
            <Button variant="outline" size="sm" className="h-8 text-[11px] px-2.5 shrink-0" onClick={() => { setActive(null); setHistory([]); setLogContacts([]); setAddContactOpen(false); }}>Close</Button>
            <Button size="sm" onClick={submit} className="h-8 text-[11px] px-3 shrink-0 bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="crmr-submit">Save Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Contact Deletion */}
      <Dialog open={!!deleteContactReq} onOpenChange={(o) => { if (!o) setDeleteContactReq(null); }}>
        <DialogContent className="max-w-md" data-testid="crmr-log-delete-dialog">
          <DialogHeader>
            <DialogTitle>Request Contact Deletion</DialogTitle>
          </DialogHeader>
          {deleteContactReq && (
            <div className="space-y-2.5 text-[12.5px]">
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
                <b>{deleteContactReq.contact.name || "(no name)"}</b> · {deleteContactReq.contact.number}
                {deleteContactReq.contact.role && <span className="text-slate-500"> · {deleteContactReq.contact.role}</span>}
                <div className="text-[11px] text-slate-500 mt-0.5">From <b>{active?.client_name}</b></div>
              </div>
              <div>
                <Label className="text-[10.5px] uppercase font-bold text-slate-600">Reason (min 5 chars)</Label>
                <Textarea
                  rows={3}
                  value={deleteContactReq.remark}
                  onChange={(e) => setDeleteContactReq((r) => ({ ...r, remark: e.target.value }))}
                  placeholder="e.g. Person has left the school / wrong number / duplicate"
                  className="mt-1"
                  data-testid="crmr-log-delete-remark"
                />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                A Master Admin must approve this request before the contact is removed from the client database.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteContactReq(null)}>Cancel</Button>
            <Button disabled={contactBusy} onClick={submitDeleteRequest} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="crmr-log-delete-submit">
              {contactBusy ? "Submitting…" : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Contact Transfer */}
      <Dialog open={!!transferContactReq} onOpenChange={(o) => { if (!o) setTransferContactReq(null); }}>
        <DialogContent className="max-w-md" data-testid="crmr-log-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Request Contact Transfer to Another School</DialogTitle>
          </DialogHeader>
          {transferContactReq && (
            <div className="space-y-2.5 text-[12.5px]">
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
                <b>{transferContactReq.contact.name || "(no name)"}</b> · {transferContactReq.contact.number}
                {transferContactReq.contact.role && <span className="text-slate-500"> · {transferContactReq.contact.role}</span>}
                <div className="text-[11px] text-slate-500 mt-0.5">Currently at <b>{active?.client_name}</b> · U-DISE {active?.u_dise || "—"}</div>
              </div>
              <div>
                <Label className="text-[10.5px] uppercase font-bold text-slate-600">Target School U-DISE</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={transferContactReq.target_udise}
                    onChange={(e) => setTransferContactReq((t) => ({ ...t, target_udise: e.target.value, lookup: null }))}
                    placeholder="11-digit U-DISE code"
                    data-testid="crmr-log-transfer-udise"
                  />
                  <Button size="sm" variant="outline" onClick={lookupTransferTarget} data-testid="crmr-log-transfer-lookup">Lookup</Button>
                </div>
                {transferContactReq.lookup && (
                  transferContactReq.lookup.exists ? (
                    <div className="mt-1.5 text-[11.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
                      ✓ Found: <b>{transferContactReq.lookup.client_name}</b>
                      {transferContactReq.lookup.assigned_to_name && <span className="text-slate-600"> · assigned to {transferContactReq.lookup.assigned_to_name}</span>}
                    </div>
                  ) : (
                    <div className="mt-1.5 text-[11.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5">
                      No school found with that U-DISE. Admin will reject if missing — add the school to the client DB first.
                    </div>
                  )
                )}
              </div>
              <div>
                <Label className="text-[10.5px] uppercase font-bold text-slate-600">Reason (min 5 chars)</Label>
                <Textarea
                  rows={3}
                  value={transferContactReq.remark}
                  onChange={(e) => setTransferContactReq((t) => ({ ...t, remark: e.target.value }))}
                  placeholder="e.g. Person has moved to a different school as Principal"
                  className="mt-1"
                  data-testid="crmr-log-transfer-remark"
                />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                On approval the contact is removed from the current school and added to the school matching this U-DISE.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTransferContactReq(null)}>Cancel</Button>
            <Button disabled={contactBusy} onClick={submitTransferRequest} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="crmr-log-transfer-submit">
              {contactBusy ? "Submitting…" : "Submit Transfer Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GeMSupportDialog open={gemOpen} onOpenChange={setGemOpen} client={active} />
      <QuoteRequestDialog open={quoteOpen} onOpenChange={setQuoteOpen} client={active} />

      {/* Reuse the exact same Training Request Dialog as Trainers → Raise Training Request */}
      <TrainingRequestDialog
        open={needTrainerOpen}
        onClose={() => setNeedTrainerOpen(false)}
        form={needTrainerForm}
        setForm={setNeedTrainerForm}
        trainers={trainersList}
        activeEmployees={activeEmployees}
        onSubmit={async () => {
          try {
            await api.post("/training-requests", needTrainerForm);
            toast.success("Training request submitted — pending approval");
            setNeedTrainerOpen(false);
            setNeedTrainerForm(emptyReqForm());
          } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
        }}
      />

      {/* Client Details Drawer */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDetailHistory([]); setDetailExtra(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading">{detail?.client_name}</DialogTitle>
            <div className="text-[11px] mono text-slate-500">U-DISE · {detail?.u_dise || "—"}</div>
          </DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Info label="Client Type" value={detail.client_type} />
                <Info label="Client Email" value={detail.client_email} />
                <Info label="Address" value={detail.address} full />
                <Info label="District" value={detail.district} />
                <Info label="State" value={detail.state} />
                <Info label="Pin Code" value={detail.pin_code} />
                <Info label="Assigned To" value={detail.assigned_to_name} />
                <Info label="WhatsApp Group" value={detail.has_whatsapp_group ? `Yes — ${detail.whatsapp_group_name || ""}` : (detail.whatsapp_no_reason || "No")} full />
                {detail.client_type === "ATL 1.0" && (
                  <>
                    <Info label="ATL 2nd Tranche" value={detail.atl_2nd_tranche} />
                    <Info label="ATL 3rd Tranche" value={detail.atl_3rd_tranche} />
                    <Info label="Drive Folder?" value={detail.atl_has_drive} />
                    {detail.atl_drive_link && <Info label="Drive Link" value={<a className="text-[var(--pk-accent)] underline" href={detail.atl_drive_link} target="_blank" rel="noreferrer">Open</a>} />}
                    <Info label="School Funds (INR)" value={detail.atl_school_funds} />
                  </>
                )}
              </div>

              {/* Orders linked to this client */}
              {detailExtra && (
                <div data-testid="crmr-detail-orders">
                  <div className="pk-table-header mb-2 flex items-center gap-2">
                    Orders ({detailExtra.orders?.length || 0})
                  </div>
                  {!detailExtra.orders || detailExtra.orders.length === 0 ? (
                    <div className="text-[12px] text-slate-400 italic">No orders linked to this client yet.</div>
                  ) : (
                    <div className="overflow-x-auto border rounded-md">
                      <table className="w-full text-[12px] responsive-table">
                        <thead className="bg-slate-50 border-b">
                          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                            <th className="px-2 py-2 font-semibold">Order #</th>
                            <th className="px-2 py-2 font-semibold">Created</th>
                            <th className="px-2 py-2 font-semibold">Type</th>
                            <th className="px-2 py-2 font-semibold">Invoice #</th>
                            <th className="px-2 py-2 font-semibold">Invoice Amt</th>
                            <th className="px-2 py-2 font-semibold">Dispatch</th>
                            <th className="px-2 py-2 font-semibold">Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailExtra.orders.map((o) => (
                            <tr key={o.id} className="border-b last:border-0" data-testid={`crmr-order-${o.id}`}>
                              <td data-label="Order #" className="px-2 py-1.5 mono font-semibold">{o.order_id || "—"}</td>
                              <td data-label="Created" className="px-2 py-1.5 text-[10px] text-slate-500 mono">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "short", day: "2-digit" }) : "—"}</td>
                              <td data-label="Type" className="px-2 py-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${o.order_type === "Service" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}>{o.order_type}</span>
                              </td>
                              <td data-label="Invoice #" className="px-2 py-1.5 mono text-[11px]">{o.invoice_no || <span className="text-slate-400">—</span>}</td>
                              <td data-label="Invoice Amt" className="px-2 py-1.5 mono">{o.invoice_amount ? `₹ ${Number(o.invoice_amount).toLocaleString("en-IN")}` : "—"}</td>
                              <td data-label="Dispatch" className="px-2 py-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${(o.dispatch_status || "").toLowerCase().includes("deliver") ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{o.dispatch_status || "—"}</span>
                              </td>
                              <td data-label="Payment" className="px-2 py-1.5">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${o.payment_status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{o.payment_status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Documentation Team + status */}
              {detailExtra && (detailExtra.documentation_required || detailExtra.documentation_assignees?.length > 0) && (
                <div data-testid="crmr-detail-doc">
                  <div className="pk-table-header mb-2">Documentation</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]">
                    <div className="p-3 border rounded-md bg-violet-50/40 border-violet-200">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-1">Assigned Team</div>
                      {detailExtra.documentation_assignees?.length > 0 ? (
                        detailExtra.documentation_assignees.map((u) => (
                          <div key={u.id} className="flex items-center justify-between py-0.5">
                            <span className="font-semibold text-slate-800">{u.name}</span>
                            <span className="text-[10px] text-slate-500">{u.role || ""}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-[11px] text-slate-400 italic">Not yet assigned</div>
                      )}
                    </div>
                    <div className="p-3 border rounded-md bg-slate-50/60">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">Status &amp; Stage</div>
                      <div className="text-slate-800"><strong>Lifecycle:</strong> {detailExtra.doc_lifecycle || "—"}</div>
                      <div className="text-slate-800"><strong>Current Stage:</strong> {detailExtra.current_doc_stage || "—"}</div>
                    </div>
                  </div>
                </div>
              )}

              {Array.isArray(detail.contacts) && detail.contacts.length > 0 && (
                <div>
                  <div className="pk-table-header mb-2">Contacts ({detail.contacts.length})</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {detail.contacts.map((c, i) => (
                      <div key={c._uid || `${c.phone || c.email || c.name || ""}-${i}`} className="border border-slate-200 rounded-md p-3 text-[12px]" data-testid={`crmr-detail-contact-${i}`}>
                        <div className="font-semibold text-slate-900">{c.name || "—"} <span className="text-[10px] text-slate-500 font-normal">· {c.type || "—"}</span></div>
                        {c.email && <div className="mono text-slate-600 truncate">{c.email}</div>}
                        {c.phone && <div className="mono text-slate-600">{c.phone}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="pk-table-header">Full Activity Log ({detailHistory.length})</div>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { const c = detail; setDetail(null); setDetailHistory([]); openLog(c); }} data-testid="crmr-detail-log">
                    + New Report
                  </Button>
                </div>
                <div className="space-y-2">
                  {detailHistory.length === 0 ? (
                    <div className="text-xs text-slate-400 italic text-center py-6 border border-dashed border-slate-200 rounded-md">No reports logged yet for this client.</div>
                  ) : detailHistory.map((h) => (
                    <div key={h.id} className="border border-slate-200 rounded-md p-3 text-[12px]" data-testid={`crmr-detail-history-${h.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge className={LEAD_COLOR[h.lead_status] || "bg-slate-100"}>{h.lead_status}</Badge>
                          <span className="text-slate-500 text-[11px]">via {h.connection_method}</span>
                        </div>
                        <span className="mono text-[10px] text-slate-400">{fmtDateTime(h.created_at)}</span>
                      </div>
                      <div className="text-slate-700 mt-1.5">{h.remark}</div>
                      <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-500">
                        {h.next_follow_up ? <span className="mono">Next follow-up: {h.next_follow_up}</span> : <span />}
                        <span>Submitted by <span className="font-semibold text-slate-700">{h.created_by_name || "—"}</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetail(null); setDetailHistory([]); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales Collaboration Dialog (Lead Owner adds / removes collaborators) */}
      <Dialog open={!!collabOpen} onOpenChange={(o) => !o && setCollabOpen(null)}>
        <DialogContent className="max-w-xl" data-testid="crmr-collab-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Collaborate on Lead · {collabOpen?.client_name}</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">Pick a teammate and explain what help you need. The lead will appear in their My Clients with a "SHARED LEAD" tag. You can remove a collaborator at any time.</div>
          </DialogHeader>

          {(collabOpen?.collaborators || []).length > 0 && (
            <div className="space-y-1.5 mb-3" data-testid="crmr-collab-list">
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Current Collaborators ({(collabOpen?.collaborators || []).length})</Label>
              {(collabOpen?.collaborators || []).map((c) => (
                <div key={c.user_id} className="flex items-start justify-between gap-2 border border-slate-200 rounded-md p-2.5 bg-slate-50/40" data-testid={`crmr-collab-row-${c.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900">{c.user_name} <span className="text-[10.5px] text-slate-500 font-normal">· {c.user_role || ""}</span></div>
                    <div className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-wrap break-words">{c.remark}</div>
                    <div className="text-[10px] text-slate-400 mt-1">Added {c.added_at ? new Date(c.added_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""} by {c.added_by_name}</div>
                  </div>
                  <button onClick={() => removeCollab(collabOpen.id, c.user_id)} className="shrink-0 h-7 w-7 grid place-items-center rounded-md text-rose-600 hover:bg-rose-50 border border-rose-200" title="Remove collaborator" data-testid={`crmr-collab-remove-${c.user_id}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3 border-t pt-3">
            <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700">Invite a Collaborator</Label>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Employee *</Label>
              <Select value={collabPick} onValueChange={setCollabPick}>
                <SelectTrigger className="h-10 mt-1.5" data-testid="crmr-collab-pick"><SelectValue placeholder="Pick a teammate…" /></SelectTrigger>
                <SelectContent>
                  {(collabPeople || [])
                    .filter((p) => !p.is_temp_account && p.id !== user?.id && p.id !== collabOpen?.assigned_to_id)
                    .filter((p) => !(collabOpen?.collaborators || []).some((c) => c.user_id === p.id))
                    .map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">What help do you need? *</Label>
              <Textarea rows={3} value={collabRemark} onChange={(e) => setCollabRemark(e.target.value)} placeholder="e.g. Need help following up with the principal next week — please join the call." className="mt-1.5" data-testid="crmr-collab-remark" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCollabOpen(null)}>Close</Button>
            <Button onClick={saveCollab} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="crmr-collab-save">
              <Users size={12} className="mr-1" /> Add Collaborator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Have-Funds Dialog — asks amount for ATL 1.0, offers "Mark as Hot" toggle */}
      <HaveFundsDialog
        open={!!fundsClient}
        onClose={() => setFundsClient(null)}
        client={fundsClient?.client || null}
        initialAmount={fundsClient?.amount || ""}
        initialHot={!!fundsClient?.is_hot}
        onSaved={() => { setFundsClient(null); load(); }}
      />

      {/* Call-Through picker */}
      <CallThroughDialog
        open={callThroughOpen}
        onClose={() => setCallThroughOpen(false)}
        clients={clients}
        reports={reports}
        followupByClient={followupByClient}
        todayStr={todayStr}
        availableClientTypes={availableClientTypes}
        onStart={(session) => {
          setCallSession(session);
          setCallThroughOpen(false);
          // Kick off with the first target
          const firstId = session.mode === "random"
            ? session.queue[Math.floor(Math.random() * session.queue.length)]
            : session.queue[0];
          const c = clients.find((x) => x.id === firstId);
          setCallSession((s) => (s ? { ...s, current: c } : s));
          if (c) setTimeout(() => openLog(c), 100);
        }}
      />
    </div>
  );
}

function MapExistingDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [u_dise, setUdise] = useState("");
  const [match, setMatch] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) { setUdise(""); setMatch(null); setReason(""); setChecking(false); }
  }, [open]);

  const checkUdise = async () => {
    const u = u_dise.trim();
    if (!u) { toast.error("Enter a U-DISE number"); return; }
    setChecking(true); setMatch(null);
    try {
      const { data } = await api.get(`/crm/udise-check/${encodeURIComponent(u)}`);
      if (!data.exists) { toast.error("No school found with this U-DISE in Client Database"); }
      else setMatch(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setChecking(false);
  };

  const submit = async () => {
    if (!match) return;
    try {
      await api.post("/crm/reassign-requests", {
        client_id: match.client_id,
        new_assignee_id: user.id,
        new_assignee_name: user.name,
        reason,
      });
      toast.success("Mapping request sent to CRM Manager / Admin");
      onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="crmr-map-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Request Existing School (by U-DISE)</DialogTitle>
          <div className="text-xs text-slate-500 mt-1">Enter the U-DISE number of a school already in the Client Database. Your request will be sent to the CRM Manager / Master Admin for approval.</div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">U-DISE Number</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={u_dise} onChange={(e) => setUdise(e.target.value)} placeholder="11-digit U-DISE" data-testid="crmr-map-udise" />
              <Button onClick={checkUdise} disabled={checking} className="h-10" data-testid="crmr-map-check">
                {checking ? "Checking…" : "Lookup"}
              </Button>
            </div>
          </div>

          {match && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3" data-testid="crmr-map-match">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">School Found</div>
              <div className="text-sm font-semibold text-slate-900 mt-1">{match.client_name}</div>
              <div className="text-[12px] text-slate-700">Currently assigned to <span className="font-semibold">{match.assigned_to_name || "— (unassigned)"}</span></div>
              <div className="mt-3">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reason for mapping to you</Label>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why should this school be mapped to you?" className="mt-1" data-testid="crmr-map-reason" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!match} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="crmr-map-submit">
            Send Mapping Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function emptyForm() {
  return {
    client_id: "",
    client_name: "",
    u_dise: "",
    connection_method: "",
    lead_status: "",
    next_follow_up: "",
    remark: "",
    connected_contact_number: "",
    connected_contact_name: "",
  };
}
function F({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


/* ============================================================================
 * DocCycleBadge — colour-coded chip showing the Documentation lifecycle of a
 * client (Completed=Green · Working=Blue · Inactive=Red). Hidden when no
 * lifecycle has been set yet.
 * ==========================================================================*/
function DocCycleBadge({ lifecycle }) {
  if (!lifecycle) return null;
  const map = {
    Completed: { cls: "bg-emerald-600 text-white", title: "Documentation lifecycle: Completed" },
    Working: { cls: "bg-blue-600 text-white", title: "Documentation lifecycle: Working" },
    Inactive: { cls: "bg-rose-600 text-white", title: "Documentation lifecycle: Inactive" },
  };
  const cfg = map[lifecycle];
  if (!cfg) return null;
  return (
    <span
      title={cfg.title}
      data-testid={`doc-cycle-badge-${lifecycle.toLowerCase()}`}
      className={`inline-flex items-center gap-0.5 ${cfg.cls} text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider`}
    >
      Doc · {lifecycle}
    </span>
  );
}



// ─────────────────────────────────────────────────────────────
// HaveFundsDialog — flags a client as having funds. If the school
// is ATL 1.0 the funds amount (INR) is required. Optionally, the
// caller can flip the "Hot" flag from the same dialog.
// ─────────────────────────────────────────────────────────────
function HaveFundsDialog({ open, onClose, client, initialAmount = "", initialHot = false, onSaved }) {
  const [amount, setAmount] = useState(initialAmount);
  const [isHot, setIsHot] = useState(initialHot);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(initialAmount || ""); setIsHot(initialHot); } /* eslint-disable-next-line */ }, [open, client?.id]);
  if (!client) return null;
  const type = (client.client_type || "").toUpperCase();
  const isAtl1 = type.includes("ATL") && type.includes("1.0");
  const canSave = !busy && (!isAtl1 || (String(amount).trim() && Number(amount) > 0));

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/crm/clients/${client.id}/mark-funds`, {
        has_funds: true,
        is_hot: isHot,
        funds_amount: isAtl1 ? Number(amount) : (amount ? Number(amount) : undefined),
        hot_reason: isHot ? (isAtl1 ? `ATL 1.0 · ₹${amount} available in school` : "Funds confirmed by CRM") : "",
      });
      toast.success(isHot ? "Marked has-funds + Hot" : "Marked as has-funds");
      onSaved?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="crmr-have-funds-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">💰 Mark as Has Funds</DialogTitle>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            {client.client_name}{type && <span className="ml-1 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-mono">{client.client_type}</span>}
          </div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {isAtl1 ? (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-rose-700">Funds with School (INR) *</Label>
              <Input
                type="number" min="0" step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 250000"
                className="mt-1.5 h-10"
                data-testid="crmr-have-funds-amount"
                autoFocus
              />
              <div className="text-[10.5px] text-slate-500 mt-1">Required for ATL 1.0 schools — enter the amount currently available in the school account.</div>
            </div>
          ) : (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Funds Amount (optional)</Label>
              <Input
                type="number" min="0" step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Optional — INR"
                className="mt-1.5 h-10"
                data-testid="crmr-have-funds-amount"
              />
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer rounded-md border border-rose-200 bg-rose-50 dark:bg-rose-950/30 px-3 py-2" data-testid="crmr-have-funds-hot-row">
            <input
              type="checkbox"
              checked={isHot}
              onChange={(e) => setIsHot(e.target.checked)}
              className="w-4 h-4 accent-rose-600"
              data-testid="crmr-have-funds-hot"
            />
            <span className="text-[12px] font-bold text-rose-700">🔥 Also Mark as Hot Client</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!canSave} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="crmr-have-funds-save">
            {busy ? "Saving…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// CallThroughDialog — pick a filter (Type / Stage / Follow-up /
// Hot), then choose Random or Sequential; the returned session
// holds a queue of client IDs. Log-Report is opened for each,
// and only advances after a successful submit.
// ─────────────────────────────────────────────────────────────
function CallThroughDialog({ open, onClose, clients, reports, followupByClient, todayStr, availableClientTypes, onStart }) {
  const [dim, setDim] = useState("type");
  const [types, setTypes] = useState([]);
  const [stage, setStage] = useState("Current");
  const [fu, setFu] = useState("overdue");
  const [mode, setMode] = useState("sequential");

  const toggleType = (t) => setTypes((arr) => arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);

  const queue = useMemo(() => {
    if (!open) return [];
    let pool = clients.slice();
    if (dim === "type") {
      if (!types.length) return [];
      pool = pool.filter((c) => {
        const ts = [c.client_type, ...(c.client_types || [])].filter(Boolean);
        return ts.some((t) => types.includes(t));
      });
    } else if (dim === "stage") {
      pool = pool.filter((c) => (c.crm_stage || deriveStage(c)) === stage);
    } else if (dim === "followup") {
      pool = pool.filter((c) => {
        const d = followupByClient.get(c.id) || followupByClient.get(c.u_dise);
        if (!d) return fu === "none";
        if (fu === "overdue") return d < todayStr;
        if (fu === "today") return d === todayStr;
        if (fu === "upcoming") return d > todayStr;
        return true;
      });
    } else if (dim === "hot") {
      pool = pool.filter((c) => c.is_hot);
    }
    return pool.map((c) => c.id);
  }, [open, dim, types, stage, fu, clients, followupByClient, todayStr]);

  const canStart = queue.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="crmr-call-through-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneCall size={16} className="text-indigo-700" /> Start Calling</DialogTitle>
          <div className="text-[11.5px] text-slate-500 mt-0.5">Pick a target set, choose Random or Sequential, and cycle through the Log Report dialog. No client repeats within a session.</div>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Basis</Label>
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {[
                { k: "type", l: "Type" },
                { k: "stage", l: "Stage" },
                { k: "followup", l: "Follow-up" },
                { k: "hot", l: "🔥 Hot" },
              ].map(({ k, l }) => (
                <button key={k} onClick={() => setDim(k)} className={`h-8 text-[11.5px] font-semibold rounded-md border transition-all ${dim === k ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`crmr-ct-dim-${k}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {dim === "type" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Client Type (multi-select)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {availableClientTypes.map((t) => (
                  <button key={t} onClick={() => toggleType(t)} className={`h-7 px-3 text-[11px] font-semibold rounded-full border ${types.includes(t) ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`crmr-ct-type-${t.replace(/[^A-Za-z0-9]/g, "_")}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {dim === "stage" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Stage</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {STAGE_KEYS.map((k) => (
                  <button key={k} onClick={() => setStage(k)} className={`h-7 px-3 text-[11px] font-semibold rounded-full border ${stage === k ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`crmr-ct-stage-${k.replace(/\s+/g, "_")}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
          )}

          {dim === "followup" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Follow-up bucket</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {[{ k: "overdue", l: "Overdue" }, { k: "today", l: "Today" }, { k: "upcoming", l: "Upcoming" }, { k: "none", l: "No follow-up" }].map(({ k, l }) => (
                  <button key={k} onClick={() => setFu(k)} className={`h-7 px-3 text-[11px] font-semibold rounded-full border ${fu === k ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid={`crmr-ct-fu-${k}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Cycle Order</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button onClick={() => setMode("sequential")} className={`h-9 text-[12px] font-semibold rounded-md border ${mode === "sequential" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid="crmr-ct-mode-sequential">
                ↓ Sequential (1 by 1)
              </button>
              <button onClick={() => setMode("random")} className={`h-9 text-[12px] font-semibold rounded-md border ${mode === "random" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid="crmr-ct-mode-random">
                🎲 Random
              </button>
            </div>
          </div>

          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[12px]">
            <span className="uppercase font-bold text-[10px] text-slate-500 tracking-wider mr-2">Ready</span>
            <span className="font-mono font-bold text-indigo-700">{queue.length}</span>
            <span className="text-slate-500 ml-1">client{queue.length === 1 ? "" : "s"} in queue</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!canStart} onClick={() => onStart?.({ queue, mode, visited: new Set(), current: null, dim })} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="crmr-ct-start">
            <PhoneCall size={13} className="mr-1.5" /> Start ({queue.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
